import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE, API_ENDPOINTS, PUBLIC_BASE_URL, IMAGE_BASE_URL } from "../config/constants";
import styled from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { downloadPDF } from "../utils/exportHelpers";
import { getCurrencySymbol, getPrinterSafeCurrencySymbol, resolveCurrencyContext } from "../utils/currency";
import {
  resolveTaxLabelFromProfile,
  resolveTaxRegistrationLabelFromProfile,
} from "../utils/taxDetails";
import {
  resetUsbPrinterConnection,
  writeUsb,
} from "../utils/webUsbPrinter";
import { SESSION_UPDATED_EVENT } from "../utils/accountLinks";
import {
  formatSplitTaxLabel,
  getUniformSplitGstRate,
} from "../utils/gst";
import {
  buildPartyLookup,
  getCustomerDisplayLabel,
  getOrderPartyId,
} from "../utils/customerDisplay";

/* ===== Helpers ===== */
const isTakeawayName = (name) =>
  typeof name === "string" && name.toLowerCase().startsWith("takeaway");
const extractTakeawayIndex = (name) => {
  if (!name) return null;
  const lower = String(name).toLowerCase().trim();
  if (lower === "takeaway") return 1;
  const m = lower.match(/takeaway[\s-]*(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};
const formatTakeawayLabel = (name) => {
  if (!name) return "TakeAway";
  const idx = extractTakeawayIndex(name);
  return idx ? `TakeAway ${idx}` : String(name);
};
const buildTableMetaMap = (list = []) => {
  const map = {};
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    const key =
      entry?.table_key ??
      entry?.tableKey ??
      entry?.key ??
      entry?.name ??
      entry?.table ??
      entry?.tableid ??
      entry?.tableId ??
      entry?.id;
    const displayName =
      entry?.display_name ??
      entry?.displayName ??
      entry?.name ??
      entry?.table ??
      entry?.table_key ??
      entry?.tableKey ??
      entry?.tableid ??
      entry?.tableId ??
      entry?.id ??
      "";
    const typeRaw =
      entry?.table_type ?? entry?.tableType ?? entry?.tabletype ?? entry?.type;
    const type = String(typeRaw || "").toLowerCase() === "room" ? "room" : "table";
    const meta = { displayName, type };
    const addKey = (k) => {
      if (k === null || k === undefined || k === "") return;
      map[String(k)] = meta;
    };
    addKey(key);
    addKey(entry?.id);
    addKey(entry?.tableid);
    addKey(entry?.tableId);
    addKey(entry?.table_id);
  });
  return map;
};

const N = (v) => {
  const x = parseFloat(String(v ?? "0").replace(/[, ]+/g, ""));
  return Number.isFinite(x) ? x : 0;
};
const money = (v) => N(v).toFixed(2);
const getOrderPaymentMode = (order) =>
  String(
    order?.payment_mode ||
      order?.paymentMode ||
      order?.paymentmode ||
      order?.mode ||
      ""
  ).trim();
const readStoredUserSession = () => {
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};
const readStoredUserData = () => {
  const parsed = readStoredUserSession();
  return parsed?.data || parsed || null;
};
const buildRestInfoFromUserData = (u = {}) => ({
  restaurant_name: u.restaurant_name || u.restname || u.name || "Bill Sarthi",
  addressline1: u.addressline1 || u.address || "",
  addressline2: u.addressline2 || "",
  addressline3: u.addressline3 || "",
  mobile_number:
    u.mobile_number || u.phonenumber || u.phone || u.contact_number || "",
  gstno: u.gstno || u.gstin || u.gstnumber || "",
  upiaddress: u.upiaddress || u.upiid || "",
  google_review_url:
    u.googlereviewurl || u.google_review_url || u.googleReviewUrl || "",
  avatar_url: u.logo || u.restaurant_logo || u.avatar_url || u.photo || "",
});
const parseAddonIds = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((v) => Number(v)).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed))
        return parsed.map((v) => Number(v)).filter(Boolean);
    } catch {
      // fall through
    }
    return raw
      .split(/[, ]+/)
      .map((v) => Number(v))
      .filter(Boolean);
  }
  return [];
};
const buildLineKey = (productId, variantId, addonIds = []) => {
  const addonKey =
    Array.isArray(addonIds) && addonIds.length
      ? addonIds.slice().sort((a, b) => a - b).join("_")
      : "na";
  const variantKey = variantId ? `v${variantId}` : "base";
  return `p${productId}-${variantKey}-a${addonKey}`;
};
const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (
    (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
};
const normalizeRows = (value) => {
  const parsed = parseMaybeJson(value);
  if (Array.isArray(parsed)) return parsed.flatMap((entry) => normalizeRows(entry));
  if (parsed && typeof parsed === "object") return [parsed];
  return [];
};
const normalizeTaxDetailRows = (taxDetails) => {
  const lines = Array.isArray(taxDetails?.lines) ? taxDetails.lines : [];
  return lines
    .map((line) => {
      const components = Array.isArray(line?.components) ? line.components : [];
      const findComponentAmount = (needle) => {
        const match = components.find((component) =>
          String(component?.name || "")
            .toLowerCase()
            .includes(needle)
        );
        return N(match?.amount);
      };

      const taxableamount = N(
        line?.base ?? line?.taxable ?? line?.taxableamount ?? line?.taxableAmount
      );
      const rate = N(
        line?.rate ?? line?.taxRate ?? line?.percent ?? line?.percentage
      );
      const amount = N(line?.amount ?? line?.tax_amount ?? 0);
      const cgst = findComponentAmount("cgst");
      const sgst = findComponentAmount("sgst");
      const igst = cgst === 0 && sgst === 0 ? amount : 0;

      return {
        taxableamount,
        rate,
        cgst,
        sgst,
        igst,
        total: N(line?.total ?? amount ?? (igst > 0 ? igst : cgst + sgst)),
      };
    })
    .filter((row) => row.taxableamount > 0 || row.total > 0);
};
const normalizePrintableTaxBreakup = (rows, taxLabel = "GST") => {
  const normalizedRows = normalizeRows(rows)
    .map((row) => {
      const taxable = N(
        row?.taxableamount ?? row?.taxable ?? row?.taxableAmount
      );
      const rate = N(
        row?.rate ?? row?.taxRate ?? row?.percent ?? row?.percentage
      );
      const cgst = N(row?.cgst ?? row?.cgstAmount ?? row?.cgstvalue);
      const sgst = N(row?.sgst ?? row?.sgstAmount ?? row?.sgstvalue);
      const igst = N(row?.igst ?? row?.igstAmount);
      const total = N(
        row?.total ??
          row?.totalamount ??
          row?.totalGST ??
          row?.amount ??
          row?.tax_amount
      );

      return {
        taxable,
        rate,
        cgst,
        sgst,
        igst,
        total: igst > 0 ? igst : total || cgst + sgst,
      };
    })
    .filter((row) => row.total > 0);

  const splitTaxBreakup =
    String(taxLabel || "GST")
      .trim()
      .toUpperCase() === "GST" ||
    normalizedRows.some((row) => row.cgst > 0 || row.sgst > 0);

  return {
    splitTaxBreakup,
    rows: normalizedRows.map((row) =>
      splitTaxBreakup ? row : { ...row, cgst: 0, sgst: 0, total: row.igst || row.total }
    ),
  };
};

/* ===== Thermal (BLE) helpers ===== */
const ESC = 0x1b;
const GS = 0x1d;
const INIT_CMD = new Uint8Array([ESC, 0x40]);
const CUT_CMD = new Uint8Array([GS, 0x56, 0x00]);
const ALIGN_LEFT = new Uint8Array([ESC, 0x61, 0x00]);
const ALIGN_CENTER = new Uint8Array([ESC, 0x61, 0x01]);
const SIZE_ON = "\x1b\x21\x30";
const SIZE_OFF = "\x1b\x21\x00";
const BOLD_ON = "\x1b\x45\x01\x1b\x47\x01\x1b\x21\x18";
const BOLD_OFF = "\x1b\x45\x00\x1b\x47\x00\x1b\x21\x00";
const BLE_LINE_WIDTH = 32;
const NAME_W = 14;
const QTY_W = 3;
const RATE_W = 6;
const AMT_W = 7;
const PRINT_PROFILES = {
  "2in": {
    lineWidth: 32,
    nameW: 14,
    qtyW: 3,
    rateW: 6,
    amtW: 7,
    logoWidth: 140,
    qrSize: 220,
    taxCols: { taxable: 8, cgst: 7, sgst: 7, total: 9 },
  },
  "3in": {
    lineWidth: 48,
    nameW: 24,
    qtyW: 4,
    rateW: 8,
    amtW: 11,
    logoWidth: 170,
    qrSize: 280,
    taxCols: { taxable: 12, cgst: 12, sgst: 12, total: 12 },
  },
  "4in": {
    lineWidth: 64,
    nameW: 32,
    qtyW: 5,
    rateW: 10,
    amtW: 13,
    logoWidth: 200,
    qrSize: 320,
    taxCols: { taxable: 16, cgst: 16, sgst: 16, total: 16 },
  },
  a4: {
    lineWidth: 72,
    nameW: 38,
    qtyW: 6,
    rateW: 12,
    amtW: 15,
    logoWidth: 220,
    qrSize: 340,
    taxCols: { taxable: 18, cgst: 18, sgst: 18, total: 18 },
  },
};
const normalizePrinterWidthPreference = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  if (raw === "a4" || raw === "a4-size" || raw === "a4size") {
    return "a4";
  }
  if (
    raw === "4" ||
    raw === "4in" ||
    raw === "4inch" ||
    raw === "4-inch" ||
    raw === "4 in" ||
    raw === "112mm" ||
    raw === "104mm" ||
    raw === "102mm" ||
    raw === "100mm"
  ) {
    return "4in";
  }
  if (
    raw === "3" ||
    raw === "3in" ||
    raw === "3inch" ||
    raw === "3-inch" ||
    raw === "80mm" ||
    raw === "76mm" ||
    raw === "72mm" ||
    raw === "80"
  ) {
    return "3in";
  }
  if (
    raw === "2" ||
    raw === "2in" ||
    raw === "2inch" ||
    raw === "2-inch" ||
    raw === "58mm" ||
    raw === "58"
  ) {
    return "2in";
  }
  return null;
};
const getPaperMeta = (widthKey) => {
  const normalized = normalizePrinterWidthPreference(widthKey) || "2in";
  if (normalized === "a4") {
    return {
      key: "a4",
      screenWidth: "960px",
      printWidth: "210mm",
      printSize: "A4",
      printMargin: "10mm",
    };
  }
  if (normalized === "4in") {
    return {
      key: "4in",
      screenWidth: "860px",
      printWidth: "104mm",
      printSize: "104mm",
      printMargin: "4mm",
    };
  }
  if (normalized === "3in") {
    return {
      key: "3in",
      screenWidth: "820px",
      printWidth: "80mm",
      printSize: "80mm",
      printMargin: "4mm",
    };
  }
  return {
    key: "2in",
    screenWidth: "780px",
    printWidth: "58mm",
    printSize: "58mm",
    printMargin: "4mm",
  };
};
const readStorageBool = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === "true";
};
const readStorageString = (key, fallback = "") =>
  (localStorage.getItem(key) || fallback || "").toString();
const readPrinterFeedCount = (value) => {
  const parsed = Number.parseInt(String(value ?? "0"), 10);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(12, Math.max(0, parsed));
};
const sizeToQrPixels = (size, fallback = 220) => {
  if (size === "small") return 170;
  if (size === "large") return 280;
  return fallback;
};
const sizeToLogoPixels = (size, fallback = 320) => {
  if (size === "small") return 110;
  if (size === "large") return 170;
  return Math.min(Number(fallback) || 140, 140);
};
const sizeToTextRem = (size, fallback = 1) => {
  if (size === "small") return Math.max(0.92, fallback - 0.08);
  if (size === "large") return fallback + 0.14;
  return fallback;
};
const chunkBytes = (bytes, size = 20) => {
  const out = [];
  for (let i = 0; i < bytes.length; i += size) out.push(bytes.slice(i, i + size));
  return out;
};
const centerLine = (text = "", width = BLE_LINE_WIDTH) => {
  const s = String(text || "").trim();
  if (!s) return "";
  if (s.length >= width) return s.slice(0, width);
  const gap = width - s.length;
  const left = Math.floor(gap / 2);
  return " ".repeat(left) + s + " ".repeat(gap - left);
};
const centerCol = (text = "", width = 8) => {
  const s = String(text || "").trim();
  if (s.length >= width) return s.slice(0, width);
  const gap = width - s.length;
  const left = Math.floor(gap / 2);
  return " ".repeat(left) + s + " ".repeat(gap - left);
};
const padR = (txt, w) => {
  const s = String(txt ?? "");
  return (s + " ".repeat(Math.max(0, w - s.length))).slice(0, w);
};
const padL = (txt, w) => {
  const s = String(txt ?? "");
  return (" ".repeat(Math.max(0, w - s.length)) + s).slice(-w);
};
const wrapText = (text, width) => {
  const s = String(text || "-");
  const out = [];
  let remaining = s.trim();
  while (remaining.length) {
    if (remaining.length <= width) {
      out.push(remaining);
      break;
    }
    const slice = remaining.slice(0, width + 1);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) {
      out.push(slice.slice(0, lastSpace));
      remaining = remaining.slice(lastSpace + 1);
    } else {
      out.push(remaining.slice(0, width));
      remaining = remaining.slice(width);
    }
    remaining = remaining.trimStart();
  }
  return out.length ? out : ["-"];
};
const col4 = (name, qty, rate, amt, widths = {}) => {
  const nameW = widths.nameW ?? NAME_W;
  const qtyW = widths.qtyW ?? QTY_W;
  const rateW = widths.rateW ?? RATE_W;
  const amtW = widths.amtW ?? AMT_W;
  return (
    padR(String(name ?? ""), nameW) +
    padL(String(qty ?? ""), qtyW) +
    " " +
    padL(String(rate ?? ""), rateW) +
    padL(String(amt ?? ""), amtW)
  );
};
const padLine = (label, right, width = BLE_LINE_WIDTH) => {
  const rightStr = String(right || "");
  const maxLabel = Math.max(1, width - rightStr.length - 1);
  const safeLabel = (label || "").toString().slice(0, maxLabel);
  const spaces = Math.max(1, width - safeLabel.length - rightStr.length);
  return `${safeLabel}${" ".repeat(spaces)}${rightStr}`;
};
const pairLine = (
  leftLabel,
  leftValue,
  rightLabel,
  rightValue,
  width = BLE_LINE_WIDTH
) => {
  const half = Math.max(12, Math.floor(width / 2));
  const leftText = `${leftLabel}: ${String(leftValue || "-")}`;
  const rightText = `${rightLabel}: ${String(rightValue || "-")}`;
  return padR(leftText, half) + padR(rightText, width - half);
};
const buildSizedCenteredLines = (text, width, sizeKey) => {
  const trimmed = (text || "").toString().trim();
  if (!trimmed) return [];
  const isLarge = sizeKey === "large";
  const lineWidth = isLarge ? Math.max(1, Math.floor(width / 2)) : width;
  const lines = wrapText(trimmed, lineWidth);
  if (!isLarge) return lines.map((line) => centerLine(line, width));
  return lines.map((line) => `${SIZE_ON}${centerLine(line, width)}${SIZE_OFF}`);
};
const canvasFromImage = async (src, maxWidth = 384, timeoutMs = 4000) => {
  if (!src) return null;
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => {
      reject(new Error("Image load timeout"));
    }, timeoutMs);
    if (/^https?:\/\//i.test(src)) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      clearTimeout(timer);
      const ratio = img.width ? Math.min(1, maxWidth / img.width) : 1;
      const w = Math.max(1, Math.round(img.width * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas);
    };
    img.onerror = (e) => {
      clearTimeout(timer);
      reject(e);
    };
    img.src = src;
  });
};
const canvasToEscPosRaster = (canvas) => {
  if (!canvas) return null;
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height).data;
  const bytesPerRow = Math.ceil(width / 8);
  const totalBytes = bytesPerRow * height;
  const raster = new Uint8Array(8 + totalBytes);
  raster[0] = 0x1d;
  raster[1] = 0x76;
  raster[2] = 0x30;
  raster[3] = 0x00;
  raster[4] = bytesPerRow & 0xff;
  raster[5] = (bytesPerRow >> 8) & 0xff;
  raster[6] = height & 0xff;
  raster[7] = (height >> 8) & 0xff;
  for (let y = 0; y < height; y++) {
    for (let xByte = 0; xByte < bytesPerRow; xByte++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = xByte * 8 + bit;
        if (x >= width) continue;
        const idx = (y * width + x) * 4;
        const r = imageData[idx];
        const g = imageData[idx + 1];
        const b = imageData[idx + 2];
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum < 160) byte |= 1 << (7 - bit);
      }
      raster[8 + y * bytesPerRow + xByte] = byte;
    }
  }
  return raster;
};
const concatUint8 = (...parts) => {
  const total = parts.reduce((sum, part) => sum + (part?.length || 0), 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    if (!part?.length) return;
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};
const buildEscPosQrCommands = (text, opts = {}) => {
  const payload = String(text || "").trim();
  if (!payload) return [];
  const model = Number(opts?.model) === 1 ? 49 : 50;
  const moduleSize = Math.max(3, Math.min(8, Number(opts?.moduleSize) || 6));
  const ecc = Math.max(48, Math.min(51, Number(opts?.ecc) || 48));
  const data = new TextEncoder().encode(payload);
  const len = data.length + 3;
  const pL = len & 0xff;
  const pH = (len >> 8) & 0xff;
  const storePrefix = new Uint8Array([GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30]);
  return [
    new Uint8Array([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, model, 0x00]),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, moduleSize]),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, ecc]),
    concatUint8(storePrefix, data),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]),
  ];
};
const normalizeLogoCandidates = (url) => {
  if (!url) return [];
  const trimmed = String(url).trim();
  if (!trimmed) return [];
  if (/^data:image\//i.test(trimmed) || /^blob:/i.test(trimmed)) {
    return [trimmed];
  }
  let primary = trimmed;
  if (!/^https?:\/\//i.test(primary)) {
    const cleanRel = primary.replace(/^\/+/, "");
    const cleanBase = (IMAGE_BASE_URL || "").replace(/\/+$/, "");
    primary = cleanBase ? `${cleanBase}/${cleanRel}` : cleanRel;
  }
  const candidates = [];
  if (/^https?:\/\//i.test(primary)) {
    try {
      const stripped = primary.replace(/^https?:\/\//i, "");
      candidates.push(`https://images.weserv.nl/?url=${encodeURIComponent(stripped)}`);
    } catch {
      // ignore
    }
  }
  candidates.push(primary);
  return candidates;
};
const logoRasterCache = new Map();
const logoRasterFromUrl = async (url, size = 140) => {
  const input = String(url || "").trim();
  if (!input) return null;
  const safeSize = Number(size) > 0 ? Number(size) : 140;
  const key = `${input}::${safeSize}`;
  if (logoRasterCache.has(key)) return logoRasterCache.get(key);
  const choices = normalizeLogoCandidates(input);
  for (const candidate of choices) {
    try {
      const logoCanvas = await canvasFromImage(candidate, safeSize, 3500);
      const logoCmd = canvasToEscPosRaster(logoCanvas);
      if (logoCmd) {
        logoRasterCache.set(key, logoCmd);
        return logoCmd;
      }
    } catch {
      // try next candidate
    }
  }
  return null;
};

const extractGSTDetails = (details, breakup, items = []) => {
  const detailRows = normalizeRows(details);
  const totalGST = detailRows.reduce(
    (sum, row) =>
      sum +
      N(
        row?.gst ??
          row?.totalGST ??
          row?.total_gst ??
          row?.gst_total ??
          row?.tax
      ),
    0
  );
  const rows = normalizeRows(breakup);
  if (!totalGST && rows.length) {
    const sum = rows.reduce((acc, r) => {
      const cgst = N(r?.cgst ?? r?.cgstAmount);
      const sgst = N(r?.sgst ?? r?.sgstAmount);
      const igst = N(r?.igst ?? r?.igstAmount);
      const row = igst > 0 ? igst : cgst + sgst;
      return acc + row;
    }, 0);
    return { totalGST: sum, rows };
  }
  if (!totalGST && Array.isArray(items) && items.length) {
    const sum = items.reduce((acc, item) => {
      const qty = N(item?.qty ?? item?.quantity ?? item?.qtyy ?? 0);
      const tax = N(
        item?.taxamount ??
          item?.tax_amount ??
          N(item?.cgst ?? item?.cgstAmount ?? 0) +
            N(item?.sgst ?? item?.sgstAmount ?? 0) +
            N(item?.igst ?? item?.igstAmount ?? 0)
      );
      return acc + qty * tax;
    }, 0);
    return { totalGST: sum, rows };
  }
  return { totalGST, rows };
};

const computeDiscount = ({
  total,
  discount,
  discount_type,
  discount_amount,
  discount_percent,
  gstTotal,
}) => {
  let pct = 0,
    flat = 0;
  if (N(discount_percent) > 0) pct = N(discount_percent);
  else if ((discount_type || "").toLowerCase() === "percentage")
    pct = N(discount);
  else if (N(discount_amount) > 0) flat = N(discount_amount);
  else {
    const d = N(discount);
    if (d > 0 && d <= 100) pct = d;
    else flat = d;
  }
  const amtFromPct = pct > 0 ? (N(total) * pct) / 100 : 0;
  const amount = flat > 0 ? flat : amtFromPct;
  const taxable = Math.max(0, N(total) - N(gstTotal));
  return {
    percent: pct,
    amount: Number(amount.toFixed(2)),
    taxable: Number(taxable.toFixed(2)),
  };
};

const formatDateTime = (raw) => {
  if (!raw) return "-";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw);
  const pad = (v) => String(v).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const PRINTER_PROFILE_API_BASE = API_BASE;
const SUPPORT_WHATSAPP = "919988777409";
const DEFAULT_RECHARGE_AMOUNTS = [499, 999, 1999];
const digitsOnly = (value) => String(value || "").replace(/\D+/g, "");
const formatDateInputValue = (raw) => {
  const date = raw ? new Date(raw) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const formatTimeInputValue = (raw) => {
  const date = raw ? new Date(raw) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};
const mergeDateAndTime = (dateValue, timeValue) => {
  if (!dateValue) return null;
  const safeTime = timeValue || "00:00";
  const merged = new Date(`${dateValue}T${safeTime}:00`);
  return Number.isNaN(merged.getTime()) ? null : merged;
};
const formatBillDateForApi = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const formatBillTimeForApi = (date) => {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};
const openSupportWhatsApp = (message) => {
  const url = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
    String(message || "").trim()
  )}`;
  window.open(url, "_blank", "noopener,noreferrer");
};
const tryPrinterProfileRequest = async (path, buildRequest) => {
  const bases = [API_BASE, PRINTER_PROFILE_API_BASE];
  let lastError = null;

  for (const base of bases) {
    try {
      const response = await fetch(`${base}${path}`, buildRequest());
      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.data) {
        return { response, result, base };
      }
      lastError = new Error(result?.message || `HTTP ${response.status}`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Request failed");
};

const PrintScreen = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { state } = useLocation();

  const initialOrder = state?.order || null;
  const [userData, setUserData] = useState(() => readStoredUserData());
  const currencyContext = resolveCurrencyContext(userData);
  const currencySymbol = getCurrencySymbol(currencyContext);
  const printerCurrencySymbol = getPrinterSafeCurrencySymbol(currencyContext);
  const profileTaxLabel = resolveTaxLabelFromProfile(userData);
  const taxRegistrationLabel = resolveTaxRegistrationLabelFromProfile(userData);

  const [order, setOrder] = useState(initialOrder);
  const [orderDetailResponse, setOrderDetailResponse] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(!initialOrder);
  const [error, setError] = useState("");
  const [variantMap, setVariantMap] = useState({});
  const [variantCountByProduct, setVariantCountByProduct] = useState({});
  const [addonsById, setAddonsById] = useState({});
  const [tableMetaMap, setTableMetaMap] = useState({});
  const [blePrinting, setBlePrinting] = useState(false);
  const [bleStatus, setBleStatus] = useState("");
  const [usbPrinting, setUsbPrinting] = useState(false);
  const [usbStatus, setUsbStatus] = useState("");
  const [printerWidthSetting, setPrinterWidthSetting] = useState("2in");
  const [printerFeedCountSetting, setPrinterFeedCountSetting] = useState(0);
  const [showTaxBreakupOnBillSetting, setShowTaxBreakupOnBillSetting] = useState(true);
  const [restaurantNameSizeSetting, setRestaurantNameSizeSetting] = useState("small");
  const [customerNameSizeSetting, setCustomerNameSizeSetting] = useState("small");
  const [showPaymentModeOnBillSetting, setShowPaymentModeOnBillSetting] = useState(true);
  const [showLogoOnBillSetting, setShowLogoOnBillSetting] = useState(true);
  const [showQrOnBillSetting, setShowQrOnBillSetting] = useState(true);
  const [alwaysPrintUpiQrSetting, setAlwaysPrintUpiQrSetting] = useState(false);
  const [printTokenNoSetting, setPrintTokenNoSetting] = useState(true);
  const [printGoogleReviewQrSetting, setPrintGoogleReviewQrSetting] =
    useState(false);
  const [boldRestaurantNameSetting, setBoldRestaurantNameSetting] = useState(true);
  const [boldGrandTotalSetting, setBoldGrandTotalSetting] = useState(true);
  const [logoSizeSetting, setLogoSizeSetting] = useState("medium");
  const [upiQrSizeSetting, setUpiQrSizeSetting] = useState("large");
  const [googleReviewQrSizeSetting, setGoogleReviewQrSizeSetting] =
    useState("small");
  const [gstPrintFormatSetting, setGstPrintFormatSetting] = useState("both");
  const [businessNameSizeSetting, setBusinessNameSizeSetting] =
    useState("medium");
  const [totalAmountSizeSetting, setTotalAmountSizeSetting] = useState("medium");
  const [savingAmountSizeSetting, setSavingAmountSizeSetting] =
    useState("medium");
  const [showPhoneOnBillSetting, setShowPhoneOnBillSetting] = useState(true);
  const [showThanksOnBillSetting, setShowThanksOnBillSetting] = useState(true);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [upiModalOpen, setUpiModalOpen] = useState(false);
  const [billMetaModalOpen, setBillMetaModalOpen] = useState(false);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [pickedLogoFile, setPickedLogoFile] = useState(null);
  const [pickedLogoPreview, setPickedLogoPreview] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [upiValue, setUpiValue] = useState("");
  const [upiSaving, setUpiSaving] = useState(false);
  const [editBillNo, setEditBillNo] = useState("");
  const [editBillDate, setEditBillDate] = useState("");
  const [editBillTime, setEditBillTime] = useState("");
  const [savingBillMeta, setSavingBillMeta] = useState(false);
  const [whatsAppNumber, setWhatsAppNumber] = useState("");
  const [sharingInvoice, setSharingInvoice] = useState(false);
  const [qrEntitlement, setQrEntitlement] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState(999);
  const [partyLookup, setPartyLookup] = useState({});
  const printerCharRef = useRef(null);
  const receiptRef = useRef(null);
  const lastBleDeviceIdRef = useRef(
    localStorage.getItem("ble_last_device_id") ||
      localStorage.getItem("printerConnectedId") ||
      ""
  );

  const [restInfo, setRestInfo] = useState(() =>
    buildRestInfoFromUserData(userData || {})
  );

  useEffect(() => {
    const info = buildRestInfoFromUserData(userData || {});
    setRestInfo(info);
    if (!upiModalOpen) {
      setUpiValue((info.upiaddress || "").trim());
    }
  }, [userData, upiModalOpen]);
  const resolvedRestaurantId = useMemo(
    () =>
      Number(
        userData?.id ||
          order?.restaurantid ||
          initialOrder?.restaurantid ||
          orderDetailResponse?.orderdetails?.restaurantid ||
          0
      ) || 0,
    [userData, order, initialOrder, orderDetailResponse]
  );
  const resolvedPartyId = useMemo(
    () =>
      getOrderPartyId(
        order || initialOrder || orderDetailResponse?.orderdetails || {}
      ),
    [order, initialOrder, orderDetailResponse]
  );
  const customFieldRows = useMemo(() => {
    const values = [
      (userData?.customfield1 || "").toString().trim(),
      (userData?.customfield2 || "").toString().trim(),
      (userData?.customfield3 || "").toString().trim(),
    ].filter(Boolean);
    return values.map((value, index) => ({
      label: `Custom Field ${index + 1}`,
      value,
    }));
  }, [userData]);

  useEffect(() => {
    const printerWidthKey = "printerWidth";
    const printerWidthLegacyKey = "printer_width";
    const taxBreakupKey = "pos_show_tax_breakup_on_bill";
    const printerFeedCountKey = "pos_printer_feed_count";
    const restaurantNameSizeKey = "pos_restaurant_name_size";
    const customerNameSizeKey = "pos_customer_name_size";
    const paymentModeOnBillKey = "pos_show_payment_mode_on_bill";
    const printLogoKey = "printer_setting_printLogoOnBill";
    const printUpiQrKey = "printer_setting_printUpiQrOnBill";
    const alwaysPrintUpiQrKey = "printer_setting_alwaysPrintUpiQr";
    const printTokenKey = "printer_setting_printTokenNo";
    const printReviewQrKey = "printer_setting_printGoogleReviewQr";
    const logoSizeKey = "printer_setting_logoSize";
    const upiQrSizeKey = "printer_setting_upiQrSize";
    const reviewQrSizeKey = "printer_setting_googleReviewQrSize";
    const gstFormatKey = "printer_setting_gstPrintFormat";
    const businessNameSizeKey = "printer_setting_businessNameSize";
    const totalAmountSizeKey = "printer_setting_totalAmountSize";
    const savingAmountSizeKey = "printer_setting_savingAmountSize";

    const boldRestaurantNameKey = "printer_setting_boldRestaurantName";
    const boldGrandTotalKey = "printer_setting_boldGrandTotal";

    const savedPrinterWidth =
      localStorage.getItem(printerWidthKey) ||
      localStorage.getItem(printerWidthLegacyKey);
    const normalizedWidth = normalizePrinterWidthPreference(savedPrinterWidth);
    if (normalizedWidth) {
      setPrinterWidthSetting(normalizedWidth);
    }
    setPrinterFeedCountSetting(
      readPrinterFeedCount(localStorage.getItem(printerFeedCountKey))
    );
    const savedTaxBreakup = localStorage.getItem(taxBreakupKey);
    if (savedTaxBreakup !== null) {
      setShowTaxBreakupOnBillSetting(savedTaxBreakup === "true");
    }
    const savedRestaurantNameSize = localStorage.getItem(restaurantNameSizeKey);
    if (savedRestaurantNameSize === "large" || savedRestaurantNameSize === "small") {
      setRestaurantNameSizeSetting(savedRestaurantNameSize);
    }
    const savedCustomerNameSize = localStorage.getItem(customerNameSizeKey);
    if (savedCustomerNameSize === "large" || savedCustomerNameSize === "small") {
      setCustomerNameSizeSetting(savedCustomerNameSize);
    }
    const savedPaymentModeOnBill = localStorage.getItem(paymentModeOnBillKey);
    if (savedPaymentModeOnBill !== null) {
      setShowPaymentModeOnBillSetting(savedPaymentModeOnBill === "true");
    }
    setShowLogoOnBillSetting(readStorageBool(printLogoKey, true));
    setShowQrOnBillSetting(readStorageBool(printUpiQrKey, true));
    setAlwaysPrintUpiQrSetting(readStorageBool(alwaysPrintUpiQrKey, false));
    setPrintTokenNoSetting(readStorageBool(printTokenKey, true));
    setPrintGoogleReviewQrSetting(readStorageBool(printReviewQrKey, false));
    setBoldRestaurantNameSetting(readStorageBool(boldRestaurantNameKey, true));
    setBoldGrandTotalSetting(readStorageBool(boldGrandTotalKey, true));
    setLogoSizeSetting(readStorageString(logoSizeKey, "medium"));
    setUpiQrSizeSetting(readStorageString(upiQrSizeKey, "large"));
    setGoogleReviewQrSizeSetting(readStorageString(reviewQrSizeKey, "small"));
    setGstPrintFormatSetting(readStorageString(gstFormatKey, "both"));
    setBusinessNameSizeSetting(readStorageString(businessNameSizeKey, "medium"));
    setTotalAmountSizeSetting(readStorageString(totalAmountSizeKey, "medium"));
    setSavingAmountSizeSetting(readStorageString(savingAmountSizeKey, "medium"));
    const syncSessionData = (rawValue = null) => {
      try {
        const parsed =
          rawValue && typeof rawValue === "string"
            ? JSON.parse(rawValue)
            : readStoredUserSession();
        const data = parsed?.data || parsed || {};
        setUserData(data);
        setShowPhoneOnBillSetting(
          String(data?.showphonenumberonprinter ?? "1") === "1"
        );
        setShowThanksOnBillSetting(
          String(data?.showthanksonbill ?? "1") === "1"
        );
      } catch {
        // ignore parse errors
      }
    };
    const stored = readStoredUserSession();
    const u = stored?.data || stored || {};
    setShowPhoneOnBillSetting(String(u?.showphonenumberonprinter ?? "1") === "1");
    setShowThanksOnBillSetting(String(u?.showthanksonbill ?? "1") === "1");
    const onStorage = (event) => {
      if (event.key === printerWidthKey || event.key === printerWidthLegacyKey) {
        const next = normalizePrinterWidthPreference(event.newValue) || "2in";
        setPrinterWidthSetting(next);
        return;
      }
      if (event.key === printerFeedCountKey) {
        setPrinterFeedCountSetting(readPrinterFeedCount(event.newValue));
        return;
      }
      if (event.key === taxBreakupKey) {
        setShowTaxBreakupOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === restaurantNameSizeKey) {
        const next = event.newValue;
        if (next === "large" || next === "small") {
          setRestaurantNameSizeSetting(next);
        }
        return;
      }
      if (event.key === customerNameSizeKey) {
        const next = event.newValue;
        if (next === "large" || next === "small") {
          setCustomerNameSizeSetting(next);
        }
        return;
      }
      if (event.key === paymentModeOnBillKey) {
        setShowPaymentModeOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === printLogoKey) {
        setShowLogoOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === printUpiQrKey) {
        setShowQrOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === alwaysPrintUpiQrKey) {
        setAlwaysPrintUpiQrSetting(event.newValue === "true");
        return;
      }
      if (event.key === printTokenKey) {
        setPrintTokenNoSetting(event.newValue === "true");
        return;
      }
      if (event.key === printReviewQrKey) {
        setPrintGoogleReviewQrSetting(event.newValue === "true");
        return;
      }
      if (event.key === boldRestaurantNameKey) {
        setBoldRestaurantNameSetting(event.newValue === "true");
        return;
      }
      if (event.key === boldGrandTotalKey) {
        setBoldGrandTotalSetting(event.newValue === "true");
        return;
      }
      if (event.key === logoSizeKey) {
        setLogoSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === upiQrSizeKey) {
        setUpiQrSizeSetting(event.newValue || "large");
        return;
      }
      if (event.key === reviewQrSizeKey) {
        setGoogleReviewQrSizeSetting(event.newValue || "small");
        return;
      }
      if (event.key === gstFormatKey) {
        setGstPrintFormatSetting(event.newValue || "both");
        return;
      }
      if (event.key === businessNameSizeKey) {
        setBusinessNameSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === totalAmountSizeKey) {
        setTotalAmountSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === savingAmountSizeKey) {
        setSavingAmountSizeSetting(event.newValue || "medium");
        return;
      }
      if (event.key === "userData") {
        syncSessionData(event.newValue || "{}");
      }
    };
    const onSessionUpdated = () => syncSessionData();
    window.addEventListener("storage", onStorage);
    window.addEventListener(SESSION_UPDATED_EVENT, onSessionUpdated);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SESSION_UPDATED_EVENT, onSessionUpdated);
    };
  }, []);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const restId = userData?.id || initialOrder?.restaurantid || order?.restaurantid;
        if (!restId) return;

        const [variantRes, addonRes] = await Promise.all([
          fetch(API_ENDPOINTS.PRODUCT_VARIANTS_LIST, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restId }),
          }),
          fetch(API_ENDPOINTS.PRODUCT_ADDONS_LIST, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restId }),
          }),
        ]);

        const variantJson = await variantRes.json().catch(() => ({}));
        const addonJson = await addonRes.json().catch(() => ({}));

        if (variantRes.ok && (variantJson?.success || Array.isArray(variantJson?.variants))) {
          const byId = {};
          const countByProduct = {};
          (variantJson.variants || []).forEach((variant) => {
            const id = Number(variant?.id);
            const productId = Number(variant?.productid);
            if (id) byId[id] = variant;
            if (productId) countByProduct[productId] = (countByProduct[productId] || 0) + 1;
          });
          setVariantMap(byId);
          setVariantCountByProduct(countByProduct);
        }

        if (addonRes.ok && (addonJson?.success || Array.isArray(addonJson?.addons))) {
          const byId = {};
          (addonJson.addons || []).forEach((addon) => {
            const id = Number(addon?.id);
            if (id) byId[id] = addon;
          });
          setAddonsById(byId);
        }
      } catch (e) {
        console.log("printscreen options load failed", e?.message || e);
      }
    };
    loadOptions();
  }, [userData?.id, initialOrder, order]);

  useEffect(() => {
    setEditBillNo(String(order?.ordercode || order?.code || order?.billno || ""));
    setEditBillDate(formatDateInputValue(order?.created_at));
    setEditBillTime(formatTimeInputValue(order?.created_at));
    setWhatsAppNumber(
      digitsOnly(
        order?.customermobile ||
          order?.customer_mobile ||
          order?.mobile ||
          order?.phone ||
          order?.party_mobile ||
          order?.partymobile ||
          ""
      )
    );
  }, [order]);

  useEffect(() => {
    if (!resolvedRestaurantId) return;
    const fetchEntitlement = async () => {
      try {
        const res = await fetch(`${API_BASE}/qr/entitlement-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: resolvedRestaurantId }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
          setQrEntitlement(json || {});
        }
      } catch (error) {
        console.log("print entitlement load failed", error?.message || error);
      }
    };
    fetchEntitlement();
  }, [resolvedRestaurantId]);

  useEffect(() => {
    if (!resolvedRestaurantId || !resolvedPartyId) return;

    let cancelled = false;
    const applyPartyList = (list = []) => {
      const lookup = buildPartyLookup(list);
      if (!lookup[resolvedPartyId] || cancelled) return false;
      setPartyLookup(lookup);
      return true;
    };

    try {
      const cached = JSON.parse(localStorage.getItem("partylistx") || "[]");
      if (Array.isArray(cached) && applyPartyList(cached)) {
        return () => {
          cancelled = true;
        };
      }
    } catch {
      // ignore cache parse errors
    }

    const fetchPartyList = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.LIST_PARTY_FOR_ORDERS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: resolvedRestaurantId }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !Array.isArray(json?.list) || cancelled) return;
        try {
          localStorage.setItem("partylistx", JSON.stringify(json.list));
        } catch {
          // ignore storage errors
        }
        applyPartyList(json.list);
      } catch {
        // party lookup is optional for print view
      }
    };

    fetchPartyList();
    return () => {
      cancelled = true;
    };
  }, [resolvedPartyId, resolvedRestaurantId]);

  // Load table metadata for labels (match mobile)
  useEffect(() => {
    const loadTables = async () => {
      try {
        const cached = localStorage.getItem("tablelistx");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) setTableMetaMap(buildTableMetaMap(parsed));
        }
      } catch {}
      const restId = userData?.id || initialOrder?.restaurantid || order?.restaurantid;
      if (!restId) return;
      try {
        const res = await fetch(
          API_ENDPOINTS.LIST_TABLE,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restId }),
          }
        );
        const json = await res.json();
        if (res.ok && Array.isArray(json?.list)) {
          setTableMetaMap(buildTableMetaMap(json.list));
          localStorage.setItem("tablelistx", JSON.stringify(json.list));
        }
      } catch (e) {
        console.log("printscreen table list load failed", e?.message || e);
      }
    };
    loadTables();
  }, [userData?.id, initialOrder, order]);

  const resolveTableLabel = useCallback(
    (raw) => {
      const value = raw === undefined || raw === null ? "" : String(raw).trim();
      if (!value || value === "0" || /take/i.test(value)) return "TA";
      const meta = tableMetaMap[value];
      return meta?.displayName || value;
    },
    [tableMetaMap]
  );

  const resolveTablePrefix = useCallback(
    (raw) => {
      const value = raw === undefined || raw === null ? "" : String(raw).trim();
      if (!value || value === "0" || /take/i.test(value)) return "Takeaway";
      const meta = tableMetaMap[value];
      return meta?.type === "room" ? "Room" : "Table";
    },
    [tableMetaMap]
  );

  /* ===== Order detail + items (getorderdetail) ===== */
  useEffect(() => {
    if (initialOrder) {
      const fromStateItems =
        (Array.isArray(initialOrder.itemslist) &&
          initialOrder.itemslist.length > 0 &&
          initialOrder.itemslist) ||
        (Array.isArray(initialOrder.orderitems) &&
          initialOrder.orderitems.length > 0 &&
          initialOrder.orderitems) ||
        (Array.isArray(initialOrder.items) && initialOrder.items.length > 0
          ? initialOrder.items
          : []);

      if (fromStateItems.length > 0) {
        setItems(fromStateItems);
        setLoading(false);
        return;
      }
    }

    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError("");

        const stored = localStorage.getItem("userData");
        if (!stored) {
          setError("Login expired. Please login again.");
          return;
        }
        const parsed = JSON.parse(stored);
        const restId = parsed?.data?.id || parsed?.id;
        if (!restId) {
          setError("Restaurant not found. Please login again.");
          return;
        }

        const body = {
          restaurantid: restId,
          orderid: orderId || initialOrder?.id,
        };

        const res = await fetch(
          API_ENDPOINTS.GET_ORDER_DETAIL,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        const json = await res.json();

        if (!res.ok) {
          throw new Error(json?.message || "Unable to load order details");
        }

        setOrderDetailResponse(json);
        const apiOrder = json?.orderdetails || json?.order || {};
        const merged = { ...(initialOrder || {}), ...apiOrder };
        setOrder(merged);

        const itemsFromApi = Array.isArray(json?.itemslist)
          ? json.itemslist
          : Array.isArray(merged.orderitems)
          ? merged.orderitems
          : Array.isArray(merged.items)
          ? merged.items
          : [];

        setItems(itemsFromApi);
      } catch (e) {
        console.error("getorderdetail error:", e);
        setError(e.message || "Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const hasOrder = !!order;

  /* ===== Meta (Bill no, etc) ===== */
  const meta = useMemo(() => {
    if (!order)
      return {
        code: "-",
        table: "-",
        createdAt: null,
        discount: 0,
        discountType: "",
        discountAmount: 0,
        discountPercent: 0,
        total: 0,
      };

    const sourceNameRaw =
      order.table_name ??
      order.tablename ??
      order.table ??
      order.tableid ??
      order.table_no ??
      "";
    const sourceName = String(sourceNameRaw || "").trim();
    const metaEntry =
      tableMetaMap[String(order?.tableid)] ||
      tableMetaMap[String(order?.table)] ||
      tableMetaMap[String(order?.table_name)] ||
      tableMetaMap[String(order?.tablename)] ||
      tableMetaMap[sourceName];

    const typeFromOrder = String(
      order?.table_type ||
        order?.tableType ||
        order?.tabletype ||
        order?.type ||
        ""
    ).toLowerCase();

    const type =
      metaEntry?.type ||
      (typeFromOrder.includes("room")
        ? "room"
        : typeFromOrder.includes("table")
        ? "table"
        : sourceName.toLowerCase().includes("room")
        ? "room"
        : "table");

    const isTakeaway =
      !sourceName ||
      sourceName === "0" ||
      sourceName === "-1" ||
      Number(sourceName) === 0 ||
      Number(sourceName) === -1 ||
      isTakeawayName(sourceName);

    const displayCandidate =
      metaEntry?.displayName ||
      order?.table_label ||
      order?.table_name ||
      order?.tablename ||
      order?.table ||
      sourceName ||
      "-";

    let tableLabel;
    let tablePrefix = "Table";
    if (isTakeaway) {
      tableLabel = formatTakeawayLabel(sourceName || "TakeAway-1");
      tablePrefix = "Takeaway";
    } else if (type === "room") {
      tablePrefix = "Room";
      tableLabel = displayCandidate.toLowerCase().startsWith("room")
        ? displayCandidate
        : `Room ${displayCandidate}`;
    } else {
      tablePrefix = "Table";
      tableLabel = displayCandidate.toLowerCase().startsWith("table")
        ? displayCandidate
        : `Table ${displayCandidate}`;
    }

    // DEBUG: log how table label is resolved (remove after verification)
    console.log("PrintScreen table label debug", {
      orderId: order?.id || order?.ordercode,
      tableid: order?.tableid,
      table: order?.table,
      table_name: order?.table_name,
      tablename: order?.tablename,
      table_type: order?.table_type || order?.tableType || order?.tabletype,
      resolvedLabel: tableLabel,
      isTakeaway,
      tablePrefix,
    });

    return {
      code: order.ordercode || order.code || order.billno || order.id,
      table: tableLabel,
      tablePrefix,
      createdAt: order.created_at,
      discount: N(order.discount),
      discountType: (order.discount_type || "").toLowerCase(),
      discountAmount: N(order.discount_amount),
      discountPercent: N(order.discount_percent),
      total: N(order.totalamount),
    };
  }, [order, tableMetaMap]);

  const gstDetails = useMemo(
    () =>
      orderDetailResponse?.gstdetails ||
      order?.gstdetails ||
      order?.gstDetails ||
      null,
    [orderDetailResponse, order]
  );

  const variantCountFallback = useMemo(() => {
    const counts = {};
    Object.values(variantMap || {}).forEach((variant) => {
      const productId = Number(variant?.productid);
      if (!productId) return;
      counts[productId] = (counts[productId] || 0) + 1;
    });
    return counts;
  }, [variantMap]);

  const displayItems = useMemo(() => {
    return (items || []).map((it, index) => {
      const productId = Number(it?.productid ?? it?.id ?? 0);
      const qty = N(it?.qty ?? it?.quantity ?? 0);
      const price = N(it?.price ?? 0);
      const priceaftertax = N(
        it?.priceaftertax ?? it?.price_after_tax ?? it?.rate_with_tax ?? it?.price ?? 0
      );
      const taxedprice = N(it?.taxedprice ?? it?.priceaftertax ?? it?.price ?? 0);
      const rate = priceaftertax || taxedprice || price;
      const addonTotal = N(it?.addon_total ?? 0);
      const variantId = it?.variantid ? Number(it?.variantid) : null;
      const addonIds = parseAddonIds(
        it?.addons_json ?? it?.addon_ids ?? it?.addons
      );
      const addonLabel = addonIds
        .map((id) => addonsById[id]?.name)
        .filter(Boolean)
        .join(", ");
      const baseName =
        it?.product_name || it?.productname || it?.name || "Item";
      const variantMeta = variantId ? variantMap[variantId] : null;
      const variantLabel = String(
        variantMeta?.label ||
          variantMeta?.name ||
          (it?.variant_label ? String(it.variant_label) : "") ||
          (it?.variantname ? String(it.variantname) : "") ||
          (it?.variant_name ? String(it.variant_name) : "") ||
          (it?.size ? String(it.size) : "") ||
          ""
      ).trim();
      const variantCount =
        Number.isFinite(variantCountByProduct[productId]) ?
          variantCountByProduct[productId] :
          variantCountFallback[productId];
      const isDefaultVariant =
        variantMeta?.is_default === 1 || variantMeta?.is_default === "1";
      const shouldShowVariant =
        variantLabel &&
        (Number.isFinite(variantCount) ? variantCount > 1 : !isDefaultVariant);
      const hasVariantSuffix =
        shouldShowVariant &&
        variantLabel &&
        String(baseName).includes(`(${variantLabel})`);
      const name = shouldShowVariant
        ? hasVariantSuffix
          ? baseName
          : `${baseName} (${variantLabel})`
        : baseName;
      const fullName = addonLabel ? `${name} + ${addonLabel}` : name;
      const lineKey = buildLineKey(productId, variantId, addonIds);

      return {
        ...it,
        id: it?.id ?? lineKey ?? `${productId}-${index}`,
        lineKey,
        productid: productId,
        qty,
        price,
        priceaftertax,
        taxedprice,
        rate,
        amount: qty * rate,
        variantid: variantId,
        addon_ids: addonIds,
        addon_label: addonLabel,
        variant_label: variantLabel,
        addon_total: addonTotal,
        base_name: baseName,
        name,
        full_name: fullName,
      };
    });
  }, [items, addonsById, variantMap, variantCountByProduct, variantCountFallback]);

  const printProfile = useMemo(
    () => PRINT_PROFILES[printerWidthSetting] || PRINT_PROFILES["2in"],
    [printerWidthSetting]
  );
  const paperMeta = useMemo(
    () => getPaperMeta(printerWidthSetting),
    [printerWidthSetting]
  );

  /* ===== GST + Discount + Total (items based) ===== */
  const calc = useMemo(() => {
    if (!order)
      return {
        breakup: [],
        totalGST: 0,
        discAmt: 0,
        final: 0,
        taxable: 0,
        gross: 0,
        taxLabel: profileTaxLabel,
      };

    const genericTaxDetails = parseMaybeJson(
      orderDetailResponse?.tax_details ||
        orderDetailResponse?.taxDetails ||
        order?.tax_details ||
        order?.taxDetails ||
        null
    );
    const genericBreakup = normalizeTaxDetailRows(genericTaxDetails);
    const breakup = genericBreakup.length
      ? genericBreakup
      : parseMaybeJson(gstDetails)?.breakup ||
        orderDetailResponse?.gstbreakup ||
        orderDetailResponse?.gst_breakup ||
        order?.gstbreakup ||
        order?.gst_breakup ||
        [];
    const extracted = extractGSTDetails(gstDetails, breakup, displayItems);
    const totalGST = N(genericTaxDetails?.tax_total) || extracted.totalGST;

    let gross = 0;
    for (const it of displayItems || []) {
      const qty = N(it?.qty ?? it?.quantity ?? it?.qtyy);
      const rate = N(
        it?.priceaftertax ??
          it?.price_after_tax ??
          it?.rate_with_tax ??
          it?.price ??
          0
      );
      gross += qty * rate;
    }

    const { amount: discAmt, taxable } = computeDiscount({
      total: meta.total,
      discount: meta.discount,
      discount_type: meta.discountType,
      discount_amount: meta.discountAmount,
      discount_percent: meta.discountPercent,
      gstTotal: totalGST,
    });

    const final = meta.total || Math.max(0, gross + totalGST - discAmt);

    return {
      breakup: extracted.rows,
      totalGST,
      discAmt,
      final,
      taxable,
      gross,
      taxLabel: genericTaxDetails?.tax_label || profileTaxLabel,
    };
  }, [order, orderDetailResponse, gstDetails, displayItems, meta, profileTaxLabel]);

  /* ===== Totals section ===== */
  const totals = useMemo(() => {
    if (!order)
      return {
        subtotal: 0,
        gst: 0,
        discount: 0,
        grand: 0,
      };

    const subtotal = calc.gross ?? 0;
    const discount = calc.discAmt ?? 0;
    const grand = calc.final ?? 0;
    const deliveryChargeValue = Number(order?.delivery_charge ?? 0) || 0;
    const inferredGst = Math.max(
      0,
      Number((grand - subtotal - deliveryChargeValue + discount).toFixed(2))
    );
    const gst = calc.totalGST > 0 ? calc.totalGST : inferredGst;
    return { subtotal, gst, discount, grand };
  }, [order, calc]);
  const customerDisplay = useMemo(
    () =>
      getCustomerDisplayLabel({
        order,
        partyLookup,
        showPaymentModeOnBill: showPaymentModeOnBillSetting,
      }),
    [order, partyLookup, showPaymentModeOnBillSetting]
  );
  const deliveryCharge = useMemo(
    () => Number(order?.delivery_charge ?? 0) || 0,
    [order]
  );
  const deliveryEnabled = useMemo(
    () => Number(order?.delivery_enabled ?? 0) === 1 || deliveryCharge > 0,
    [order, deliveryCharge]
  );
  const displayTaxLabel = useMemo(
    () => String(calc?.taxLabel || profileTaxLabel).trim() || "Tax",
    [calc, profileTaxLabel]
  );
  const invoiceFileName = useMemo(
    () => `invoice_${String(meta.code || orderId || "order").replace(/[^a-z0-9_-]+/gi, "_")}.pdf`,
    [meta.code, orderId]
  );
  const whatsAppSummaryText = useMemo(() => {
    const lines = [
      `${restInfo.restaurant_name || "Restaurant"} Invoice`,
      `Bill No: ${meta.code || "-"}`,
      `${meta.tablePrefix || "Table"}: ${meta.table || "-"}`,
      `Date: ${formatDateTime(meta.createdAt)}`,
      `Customer: ${customerDisplay || "CASH"}`,
      `Total: ${printerCurrencySymbol} ${money(totals.grand)}`,
      "",
      "Invoice PDF is ready to share.",
    ];
    return lines.join("\n");
  }, [
    restInfo.restaurant_name,
    meta.code,
    meta.tablePrefix,
    meta.table,
    meta.createdAt,
    customerDisplay,
    totals.grand,
  ]);

  /* ===== GST breakup rows ===== */
  const taxRows = useMemo(() => {
    if (!calc.breakup || !calc.breakup.length) {
      const rowsFromItems = (displayItems || [])
        .map((item) => {
          const qty = N(item?.qty ?? item?.quantity ?? item?.qtyy ?? 0);
          if (qty <= 0) return null;
          const taxable = N(
            item?.priceaftertax ??
              item?.price_after_tax ??
              item?.rate_with_tax ??
              item?.price ??
              0
          ) * qty;
          const cgst = N(item?.cgst ?? item?.cgstAmount ?? 0) * qty;
          const sgst = N(item?.sgst ?? item?.sgstAmount ?? 0) * qty;
          const igst = N(item?.igst ?? item?.igstAmount ?? 0) * qty;
          const total = N(item?.taxamount ?? item?.tax_amount ?? 0) * qty;
          const finalTotal = igst > 0 ? igst : total || cgst + sgst;
          if (finalTotal <= 0) return null;
          return {
            taxable,
            rate: 0,
            cgst: igst > 0 ? 0 : cgst,
            sgst: igst > 0 ? 0 : sgst,
            igst,
            total: finalTotal,
          };
        })
        .filter(Boolean);
      if (!rowsFromItems.length) {
        return { rows: [], splitTaxBreakup: true };
      }
      const printable = normalizePrintableTaxBreakup(rowsFromItems, displayTaxLabel);
      return {
        splitTaxBreakup: printable.splitTaxBreakup,
        rows: printable.rows.map((row) => ({
          taxable: N(row?.taxableamount ?? row?.taxable ?? row?.taxableAmount),
          rate: N(row?.rate ?? row?.taxRate ?? row?.percent ?? row?.percentage),
          cgst: N(row?.cgst ?? row?.cgstAmount ?? row?.cgstvalue ?? 0),
          sgst: N(row?.sgst ?? row?.sgstAmount ?? row?.sgstvalue ?? 0),
          igst: N(row?.igst ?? row?.igstAmount ?? 0),
          total: N(row?.total ?? row?.totalamount ?? 0),
        })),
      };
    }
    const printable = normalizePrintableTaxBreakup(calc.breakup, displayTaxLabel);
    return {
      splitTaxBreakup: printable.splitTaxBreakup,
      rows: printable.rows
      .map((row) => {
        const taxable = N(
          row?.taxableamount ?? row?.taxable ?? row?.taxableAmount
        );
        const igst = N(row?.igst ?? row?.igstAmount ?? 0);
        const cgst = igst
          ? 0
          : N(row?.cgst ?? row?.cgstAmount ?? row?.cgstvalue ?? 0);
        const sgst = igst
          ? 0
          : N(row?.sgst ?? row?.sgstAmount ?? row?.sgstvalue ?? 0);
        const totalVal =
          igst > 0 ? igst : cgst + sgst || N(row?.total ?? row?.totalamount);
        return {
          taxable,
          rate: N(row?.rate ?? row?.taxRate ?? row?.percent ?? row?.percentage),
          cgst,
          sgst,
          igst,
          total: totalVal,
        };
      })
      .filter((r) => r.total > 0),
    };
  }, [calc.breakup, displayItems, displayTaxLabel]);
  const gstSplitRate = useMemo(
    () => getUniformSplitGstRate(taxRows.rows),
    [taxRows.rows]
  );
  const taxRowsTotals = useMemo(
    () =>
      taxRows.rows.reduce(
        (acc, row) => ({
          cgst: acc.cgst + N(row?.cgst ?? 0),
          sgst: acc.sgst + N(row?.sgst ?? 0),
        }),
        { cgst: 0, sgst: 0 }
      ),
    [taxRows.rows]
  );

  const knownServiceIds = useMemo(
    () => [
      "0000ffe0-0000-1000-8000-00805f9b34fb",
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff00-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "000018f0-0000-1000-8000-00805f9b34fb",
      "000018f1-0000-1000-8000-00805f9b34fb",
    ],
    []
  );
  const knownCharIds = useMemo(
    () => [
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
    ],
    []
  );
  const rememberBleDevice = useCallback((device) => {
    try {
      const id = device?.id || "";
      if (id) {
        lastBleDeviceIdRef.current = id;
        localStorage.setItem("ble_last_device_id", id);
        localStorage.setItem("printerConnectedId", id);
      }
      localStorage.setItem(
        "printerConnected",
        String(device?.name || id || "Connected Printer")
      );
    } catch {
      // ignore storage errors
    }
  }, []);
  const resolveCharacteristicFromDevice = useCallback(
    async (device) => {
      if (!device?.gatt) return null;
      if (!device.gatt.connected) {
        await device.gatt.connect();
      }
      const server = device.gatt;
      const services = await server.getPrimaryServices();
      const orderedServices = [
        ...services.filter((svc) =>
          knownServiceIds.includes(String(svc.uuid || "").toLowerCase())
        ),
        ...services.filter(
          (svc) => !knownServiceIds.includes(String(svc.uuid || "").toLowerCase())
        ),
      ];
      for (const svc of orderedServices) {
        const chars = await svc.getCharacteristics();
        const orderedChars = [
          ...chars.filter((c) =>
            knownCharIds.includes(String(c.uuid || "").toLowerCase())
          ),
          ...chars.filter(
            (c) => !knownCharIds.includes(String(c.uuid || "").toLowerCase())
          ),
        ];
        for (const c of orderedChars) {
          const { write, writeWithoutResponse } = c.properties || {};
          if (write || writeWithoutResponse) {
            printerCharRef.current = c;
            rememberBleDevice(device);
            setBleStatus(`Connected: ${device.name || device.id}`);
            return c;
          }
        }
      }
      return null;
    },
    [knownServiceIds, knownCharIds, rememberBleDevice]
  );

  const connectBlePrinter = useCallback(async ({ allowPrompt = true } = {}) => {
    if (!navigator?.bluetooth) {
      throw new Error("Web Bluetooth not supported in this browser.");
    }
    try {
      const devices = navigator.bluetooth.getDevices
        ? await navigator.bluetooth.getDevices()
        : [];
      const lastId = lastBleDeviceIdRef.current;
      const ordered = lastId
        ? [
            ...devices.filter((d) => d.id === lastId),
            ...devices.filter((d) => d.id !== lastId),
          ]
        : devices;
      for (const device of ordered) {
        try {
          const c = await resolveCharacteristicFromDevice(device);
          if (c) return c;
        } catch {
          // try next paired device
        }
      }
    } catch {
      // continue with prompt flow
    }

    if (!allowPrompt) {
      throw new Error("No paired BLE printer found");
    }
    setBleStatus("Scanning for printers…");
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [
        0xffe0,
        0xffe5,
        0x18f0,
        0x18f1,
        0xff00,
        0xff01,
        0xff02,
        "0000ffe0-0000-1000-8000-00805f9b34fb",
        "0000ffe1-0000-1000-8000-00805f9b34fb",
        "0000ff00-0000-1000-8000-00805f9b34fb",
        "0000ff01-0000-1000-8000-00805f9b34fb",
        "0000ff02-0000-1000-8000-00805f9b34fb",
        "000018f0-0000-1000-8000-00805f9b34fb",
        "000018f1-0000-1000-8000-00805f9b34fb",
      ],
    });

    const connected = await resolveCharacteristicFromDevice(device);
    if (connected) return connected;
    throw new Error("No writable characteristic found. Printer might be classic Bluetooth.");
  }, [resolveCharacteristicFromDevice]);

  const writeBle = useCallback(async (bytes) => {
    const char = printerCharRef.current;
    if (!char) throw new Error("Printer not connected");
    const parts = chunkBytes(bytes, 20);
    const canWriteWithResponse = !!char.writeValue;
    const canWriteWithoutResponse = !!char.writeValueWithoutResponse;
    const shouldUseFastMode =
      canWriteWithoutResponse && (parts.length > 80 || !canWriteWithResponse);

    const sendWithAck = async () => {
      if (!canWriteWithResponse) throw new Error("Write-with-response not available");
      for (let i = 0; i < parts.length; i += 1) {
        await char.writeValue(parts[i]);
        await new Promise((r) => setTimeout(r, 1));
      }
    };

    const sendFast = async () => {
      if (!canWriteWithoutResponse) throw new Error("Fast write not available");
      for (let i = 0; i < parts.length; i += 1) {
        await char.writeValueWithoutResponse(parts[i]);
        await new Promise((r) => setTimeout(r, 3));
        if ((i + 1) % 30 === 0) {
          await new Promise((r) => setTimeout(r, 18));
        }
      }
    };

    if (shouldUseFastMode) {
      try {
        await sendFast();
      } catch (fastErr) {
        if (canWriteWithResponse) {
          await sendWithAck();
        } else {
          throw fastErr;
        }
      }
    } else if (canWriteWithResponse) {
      await sendWithAck();
    } else if (canWriteWithoutResponse) {
      await sendFast();
    } else {
      throw new Error("Selected printer characteristic is not writable.");
    }
  }, []);

  const tryAutoReconnect = useCallback(async () => {
    if (!navigator?.bluetooth?.getDevices) return false;
    const lastId = lastBleDeviceIdRef.current;
    try {
      const devices = await navigator.bluetooth.getDevices();
      if (!devices?.length) return false;
      const ordered = lastId
        ? [
            ...devices.filter((d) => d.id === lastId),
            ...devices.filter((d) => d.id !== lastId),
          ]
        : devices;
      for (const device of ordered) {
        try {
          const c = await resolveCharacteristicFromDevice(device);
          if (c) return true;
        } catch {
          // try next device
        }
      }
      return false;
    } catch {
      return false;
    }
  }, [resolveCharacteristicFromDevice]);

  useEffect(() => {
    tryAutoReconnect();
  }, [tryAutoReconnect]);


  /* ===== QR (UPI ya generic) ===== */
  const logoUrl = useMemo(() => {
    const rel =
      restInfo?.avatar_url ||
      restInfo?.logo ||
      userData?.avatar_url ||
      userData?.logo ||
      userData?.restaurant_logo ||
      userData?.photo ||
      "";
    if (!rel) return "";
    const trimmed = String(rel).trim();
    if (
      /^https?:\/\//i.test(trimmed) ||
      /^data:image\//i.test(trimmed) ||
      /^blob:/i.test(trimmed)
    ) {
      return trimmed;
    }
    const cleanRel = trimmed.replace(/^\/+/, "");
    const cleanBase = (IMAGE_BASE_URL || "").replace(/\/+$/, "");
    return cleanBase ? `${cleanBase}/${cleanRel}` : cleanRel;
  }, [restInfo, userData]);

  const showPhoneOnBill = showPhoneOnBillSetting && !!restInfo?.mobile_number;
  const showQRCodeOnBill = !!(restInfo?.upiaddress || "").trim();
  const paymentModeLabel = getOrderPaymentMode(order).toLowerCase();
  const shouldPrintUpiQr =
    showQrOnBillSetting &&
    showQRCodeOnBill &&
    (alwaysPrintUpiQrSetting || paymentModeLabel === "upi");
  const gstEnabled = gstPrintFormatSetting !== "none";
  const gstVisible = Number(totals.gst || 0) > 0;
  const printProfileWithSizes = useMemo(
    () => ({
      ...printProfile,
      logoWidth: sizeToLogoPixels(logoSizeSetting, printProfile.logoWidth || 320),
      qrSize: sizeToQrPixels(upiQrSizeSetting, printProfile.qrSize || 220),
    }),
    [printProfile, logoSizeSetting, upiQrSizeSetting]
  );
  const restaurantNameSizeForPrint =
    businessNameSizeSetting === "small" || businessNameSizeSetting === "large"
      ? businessNameSizeSetting
      : restaurantNameSizeSetting;

  const qrInfo = useMemo(() => {
    const upiId = (restInfo?.upiaddress || "").trim();
    if (upiId && shouldPrintUpiQr) {
      const amountStr = Number(N(calc.final) || 0).toFixed(2);
      const payee = (restInfo?.restaurant_name || "Restaurant").slice(0, 25);
      const note = `Bill ${meta.code || ""}`.trim();

      const upiUrl =
        `upi://pay?pa=${encodeURIComponent(upiId)}` +
        `&pn=${encodeURIComponent(payee)}` +
        `&am=${encodeURIComponent(amountStr)}` +
        `&cu=INR` +
        `&tn=${encodeURIComponent(note)}`;

      const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
        upiUrl
      )}`;

      return {
        type: "upi",
        qrImg,
        label: "Scan & Pay",
        upiId,
        amountStr,
      };
    }

    if (printGoogleReviewQrSetting) {
      const reviewUrl = (restInfo?.google_review_url || "").trim();
      if (reviewUrl) {
        const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
          reviewUrl
        )}`;
        return {
          type: "review",
          qrImg,
          label: "Scan to review us",
        };
      }
    }
    return null;
  }, [
    restInfo,
    calc.final,
    meta.code,
    shouldPrintUpiQr,
    printGoogleReviewQrSetting,
  ]);

  const buildThermalBillPayload = useCallback(async () => {
    const lineWidth = printProfileWithSizes.lineWidth || BLE_LINE_WIDTH;
    const nameW = printProfileWithSizes.nameW || NAME_W;
    const qtyW = printProfileWithSizes.qtyW || QTY_W;
    const rateW = printProfileWithSizes.rateW || RATE_W;
    const amtW = printProfileWithSizes.amtW || AMT_W;
    const taxCols =
      printProfileWithSizes.taxCols || PRINT_PROFILES["2in"].taxCols;

    const receiptItems = displayItems
      .filter((it) => Number(it?.qty || 0) > 0)
      .map((it) => ({
        name: it?.full_name || it?.name || "Item",
        qty: Number(it?.qty || 0),
        rate: Number(it?.rate || 0),
        total: Number(it?.amount || 0),
      }));

    const customerLabel = customerDisplay;
    const paymentMode = getOrderPaymentMode(order);
    const isThreeIn = lineWidth >= 48;
    const boldOnCmd = isThreeIn ? "\x1b\x45\x01\x1b\x47\x01\x1b\x21\x18" : "\x1b\x45\x01";
    const boldOffCmd = isThreeIn ? "\x1b\x45\x00\x1b\x47\x00\x1b\x21\x00" : "\x1b\x45\x00";
    const lines = [];
    const restNameStr = restInfo.restaurant_name || "Bill Sarthi";
    const restNameLines = wrapText(restNameStr, lineWidth);
    restNameLines.forEach((ln) => {
      const centered = centerLine(ln, lineWidth);
      if (boldRestaurantNameSetting) {
        lines.push(`${boldOnCmd}${centered}${boldOffCmd}`);
      } else {
        lines.push(centered);
      }
    });
    [restInfo.addressline1, restInfo.addressline2, restInfo.addressline3]
      .filter(Boolean)
      .forEach((ln) => lines.push(centerLine(ln, lineWidth)));
    if (showPhoneOnBillSetting && restInfo.mobile_number)
      lines.push(centerLine(`Phone: ${restInfo.mobile_number}`, lineWidth));
    if (restInfo.gstno) lines.push(centerLine(`${taxRegistrationLabel}: ${restInfo.gstno}`, lineWidth));
    lines.push(
      pairLine(
        meta.tablePrefix || "Table",
        meta.table,
        showPaymentModeOnBillSetting ? "Payment" : "",
        showPaymentModeOnBillSetting ? paymentMode || "N/A" : "",
        lineWidth
      )
    );
    lines.push(padLine("Date/Time", formatDateTime(meta.createdAt), lineWidth));
    const effectiveOrderNo = meta.code || meta.orderCode || orderId;
    if (effectiveOrderNo && customerLabel) {
      const singleLine = `Order No: ${effectiveOrderNo} Customer: ${customerLabel}`;
      if (singleLine.length <= lineWidth) {
        lines.push(
          padLine(
            `Order No: ${effectiveOrderNo}`,
            `Customer: ${customerLabel}`,
            lineWidth
          )
        );
      } else {
        lines.push(`Order No: ${effectiveOrderNo}`);
        lines.push(`Customer: ${customerLabel}`);
      }
    } else if (effectiveOrderNo) {
      lines.push(`Order No: ${effectiveOrderNo}`);
    } else if (customerLabel) {
      lines.push(`Customer: ${customerLabel}`);
    }
    if (printTokenNoSetting && meta.tokenNo) {
      lines.push(`Token No: ${meta.tokenNo}`);
    }
    lines.push("-".repeat(lineWidth));
    lines.push(col4("ITEM", "Q", "RATE", "AMT", { nameW, qtyW, rateW, amtW }));
    lines.push("-".repeat(lineWidth));
    receiptItems.forEach((it) => {
      const nameParts = wrapText(it.name, nameW);
      const rateStr = Number(it.rate || 0).toFixed(2);
      const amtStr = Number(it.total || 0).toFixed(2);
      if (nameParts.length) {
        lines.push(col4(nameParts[0], it.qty, rateStr, amtStr, { nameW, qtyW, rateW, amtW }));
        nameParts.slice(1).forEach((part) => {
          lines.push(col4(part, "", "", "", { nameW, qtyW, rateW, amtW }));
        });
      }
    });
    lines.push("-".repeat(lineWidth));
    const baseSubtotal = (gstVisible && totals.gst > 0 && totals.subtotal >= totals.gst)
      ? (totals.subtotal - totals.gst)
      : totals.subtotal;
    lines.push(padLine("Subtotal", money(baseSubtotal), lineWidth));
    if (gstVisible) {
      if (taxRows.splitTaxBreakup) {
        lines.push(
          padLine(
            formatSplitTaxLabel("CGST", gstSplitRate),
            money(taxRowsTotals.cgst),
            lineWidth
          )
        );
        lines.push(
          padLine(
            formatSplitTaxLabel("SGST", gstSplitRate),
            money(taxRowsTotals.sgst),
            lineWidth
          )
        );
      } else {
        lines.push(padLine(displayTaxLabel, money(totals.gst), lineWidth));
      }
    }
    if (totals.discount > 0)
      lines.push(padLine("Discount", `-${money(totals.discount)}`, lineWidth));
    if (deliveryEnabled && deliveryCharge > 0)
      lines.push(padLine("Delivery", money(deliveryCharge), lineWidth));

    const grandTotalLineStr = padLine("Grand Total", money(totals.grand), lineWidth);
    if (boldGrandTotalSetting) {
      lines.push(`${boldOnCmd}${grandTotalLineStr}${boldOffCmd}`);
    } else {
      lines.push(grandTotalLineStr);
    }

    if ((gstEnabled || gstVisible) && showTaxBreakupOnBillSetting && taxRows.rows.length) {
      lines.push("-".repeat(lineWidth));
      lines.push(centerLine(`${displayTaxLabel} Breakup`, lineWidth));
      if (taxRows.splitTaxBreakup) {
        lines.push(
          centerCol("Taxable", taxCols.taxable) +
            centerCol(
              formatSplitTaxLabel("CGST", gstSplitRate, { showRate: false }),
              taxCols.cgst
            ) +
            centerCol(
              formatSplitTaxLabel("SGST", gstSplitRate, { showRate: false }),
              taxCols.sgst
            ) +
            centerCol("Total", taxCols.total)
        );
      }
      taxRows.rows.forEach((r) => {
        const line = taxRows.splitTaxBreakup
          ? centerCol(money(r.taxable), taxCols.taxable) +
            centerCol(money(r.cgst), taxCols.cgst) +
            centerCol(money(r.sgst), taxCols.sgst) +
            centerCol(money(r.total), taxCols.total)
          : padLine(
              `Taxable ${money(r.taxable)}`,
              money(r.total),
              lineWidth
            );
        lines.push(line.slice(0, lineWidth));
      });
    }

    const receiptText = lines.join("\n");
    const chunks = [INIT_CMD, ALIGN_LEFT];
    const logoRasterPromise = showLogoOnBillSetting
      ? logoRasterFromUrl(logoUrl, printProfileWithSizes.logoWidth || 120)
      : Promise.resolve(null);
    if (showLogoOnBillSetting) {
      try {
        const logoCmd = await logoRasterPromise;
        if (logoCmd) {
          chunks.push(ALIGN_CENTER);
          chunks.push(logoCmd);
          chunks.push(ALIGN_LEFT);
        } else {
          console.log("Logo render skipped: all logo sources failed");
        }
      } catch (e) {
        console.log("Logo render failed", e);
      }
    }

    const enc = new TextEncoder();
    chunks.push(enc.encode(receiptText + "\n"));
    const upi = (restInfo?.upiaddress || "").trim();
    if (shouldPrintUpiQr && upi) {
      try {
        const qrModuleSize = lineWidth >= 48
          ? (upiQrSizeSetting === "small" ? 3 : upiQrSizeSetting === "large" ? 5 : 4)
          : (upiQrSizeSetting === "small" ? 3 : upiQrSizeSetting === "large" ? 4 : 3);
        const qrCommands = buildEscPosQrCommands(
          `upi://pay?pa=${encodeURIComponent(upi)}`,
          { moduleSize: qrModuleSize, ecc: 48 }
        );
        if (qrCommands.length) {
          chunks.push(enc.encode("\n"));
          chunks.push(ALIGN_CENTER);
          qrCommands.forEach((cmd) => chunks.push(cmd));
          chunks.push(enc.encode("\n"));
          chunks.push(ALIGN_LEFT);
        }
      } catch (e) {
        console.log("QR render failed", e);
      }
    }
    if (printGoogleReviewQrSetting && restInfo?.google_review_url) {
      try {
        const reviewQrModuleSize =
          googleReviewQrSizeSetting === "large"
            ? lineWidth >= 48
              ? 7
              : 5
            : googleReviewQrSizeSetting === "small"
            ? 4
            : lineWidth >= 48
            ? 6
            : 5;
        const reviewCommands = buildEscPosQrCommands(restInfo.google_review_url, {
          moduleSize: reviewQrModuleSize,
          ecc: 48,
        });
        if (reviewCommands.length) {
          chunks.push(enc.encode("\n"));
          chunks.push(ALIGN_CENTER);
          chunks.push(enc.encode("Google Review\n"));
          reviewCommands.forEach((cmd) => chunks.push(cmd));
          chunks.push(enc.encode("\n"));
          chunks.push(ALIGN_LEFT);
        }
      } catch (e) {
        console.log("Review QR render failed", e);
      }
    }

    // UNBOLD SINGLE TOTAL LINE BELOW QR CODE
    const bottomTotalStr = centerLine(`Total: ${money(totals.grand)}`, lineWidth);
    chunks.push(enc.encode("=".repeat(lineWidth) + "\n"));
    chunks.push(enc.encode(`${bottomTotalStr}\n`));
    chunks.push(enc.encode("=".repeat(lineWidth) + "\n"));

    // THANKS AT THE VERY LAST
    if (showThanksOnBillSetting) {
      chunks.push(ALIGN_CENTER);
      chunks.push(enc.encode("THANKS PLEASE VISIT AGAIN\n"));
      chunks.push(ALIGN_LEFT);
    }

    chunks.push(enc.encode("\n".repeat(printerFeedCountSetting)));
    chunks.push(CUT_CMD);

    const totalLen = chunks.reduce((s, arr) => s + arr.length, 0);
    const payload = new Uint8Array(totalLen);
    let offset = 0;
    chunks.forEach((arr) => {
      payload.set(arr, offset);
      offset += arr.length;
    });
    return payload;
  }, [
    printProfileWithSizes,
    displayItems,
    order,
    restInfo,
    restaurantNameSizeForPrint,
    showPhoneOnBillSetting,
    meta.code,
    meta.createdAt,
    meta.tablePrefix,
    meta.table,
    showPaymentModeOnBillSetting,
    totals,
    gstVisible,
    displayTaxLabel,
    deliveryEnabled,
    deliveryCharge,
    gstEnabled,
    showTaxBreakupOnBillSetting,
    taxRows,
    gstSplitRate,
    taxRowsTotals.cgst,
    taxRowsTotals.sgst,
    showThanksOnBillSetting,
    showLogoOnBillSetting,
    logoUrl,
    shouldPrintUpiQr,
    printGoogleReviewQrSetting,
    googleReviewQrSizeSetting,
    showThanksOnBillSetting,
  ]);

  const handleBlePrint = useCallback(async () => {
    if (!order) return;
    setBlePrinting(true);
    try {
      if (printerCharRef.current) {
        const activeDevice = printerCharRef.current?.service?.device;
        if (activeDevice?.gatt && !activeDevice.gatt.connected) {
          printerCharRef.current = null;
        }
      }
      if (!printerCharRef.current) {
        const autoConnected = await Promise.race([
          tryAutoReconnect(),
          new Promise((resolve) => setTimeout(() => resolve(false), 2200)),
        ]);
        if (!autoConnected || !printerCharRef.current) {
          await connectBlePrinter();
        }
      }

      const payload = await buildThermalBillPayload();

      const sendWithTimeout = () =>
        Promise.race([
          writeBle(payload),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Print timed out")), 35000)
          ),
        ]);

      try {
        await sendWithTimeout();
      } catch (firstErr) {
        printerCharRef.current = null;
        const recovered = await tryAutoReconnect();
        if (!recovered || !printerCharRef.current) throw firstErr;
        await sendWithTimeout();
      }
      setBleStatus("Printed via BLE.");
    } catch (err) {
      console.error(err);
      setBleStatus(err?.message || "Bluetooth print failed");
    } finally {
      setBlePrinting(false);
    }
  }, [
    order,
    connectBlePrinter,
    tryAutoReconnect,
    writeBle,
    buildThermalBillPayload,
  ]);

  const handleUsbPrint = useCallback(async () => {
    if (!order) return;
    setUsbPrinting(true);
    try {
      setUsbStatus("Checking USB printer...");
      const payload = await buildThermalBillPayload();
      const sendWithTimeout = (allowPrompt) =>
        Promise.race([
          writeUsb(payload, { allowPrompt, onStatus: setUsbStatus }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("USB print timed out")), 35000)
          ),
        ]);

      try {
        await sendWithTimeout(false);
      } catch {
        await resetUsbPrinterConnection();
        await sendWithTimeout(true);
      }

      setUsbStatus("Printed via USB.");
    } catch (err) {
      const message =
        err?.name === "NotFoundError"
          ? "USB printer selection cancelled."
          : err?.message || "USB print failed";
      setUsbStatus(message);
    } finally {
      setUsbPrinting(false);
    }
  }, [order, buildThermalBillPayload]);

  const updateStoredUserData = useCallback((nextData) => {
    try {
      const parsed = readStoredUserSession();
      const prevData = parsed?.data || parsed || {};
      const mergedData = { ...prevData, ...(nextData || {}) };
      const updatedPayload = parsed?.data
        ? { ...parsed, data: mergedData }
        : { ...parsed, ...mergedData };
      if (Array.isArray(updatedPayload?.account_link_context?.accounts)) {
        const activeRestaurantId = Number(
          updatedPayload?.account_link_context?.active_restaurant_id ||
            mergedData?.id ||
            mergedData?.restaurantid ||
            mergedData?.restaurant_id ||
            0
        );
        updatedPayload.account_link_context = {
          ...updatedPayload.account_link_context,
          accounts: updatedPayload.account_link_context.accounts.map((account) => {
            const accountId = Number(
              account?.id ?? account?.restaurantid ?? account?.restaurant_id ?? 0
            );
            return activeRestaurantId && accountId === activeRestaurantId
              ? { ...account, ...mergedData }
              : account;
          }),
        };
      }
      localStorage.setItem("userData", JSON.stringify(updatedPayload));
      window.dispatchEvent(new Event(SESSION_UPDATED_EVENT));
      setUserData(mergedData);
      return mergedData;
    } catch {
      localStorage.setItem("userData", JSON.stringify({ data: nextData }));
      window.dispatchEvent(new Event(SESSION_UPDATED_EVENT));
      setUserData(nextData || null);
      return nextData || null;
    }
  }, []);

  const handleLogoFileChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPickedLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setPickedLogoPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return previewUrl;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (pickedLogoPreview) URL.revokeObjectURL(pickedLogoPreview);
    };
  }, [pickedLogoPreview]);

  const handleUploadLogo = useCallback(async () => {
    if (!pickedLogoFile) {
      toast.info("Pick a logo first.");
      return;
    }
    if (!resolvedRestaurantId) {
      toast.error("Restaurant not found.");
      return;
    }
    setLogoUploading(true);
    try {
      const { result } = await tryPrinterProfileRequest("/printer/logo", () => {
        const form = new FormData();
        form.append("restaurantid", String(resolvedRestaurantId));
        form.append("photo", pickedLogoFile, pickedLogoFile.name || "logo.jpg");
        return {
          method: "POST",
          body: form,
        };
      });
      const updated = updateStoredUserData(result.data);
      setRestInfo(buildRestInfoFromUserData(updated || result.data || {}));
      setPickedLogoFile(null);
      if (pickedLogoPreview) {
        URL.revokeObjectURL(pickedLogoPreview);
      }
      setPickedLogoPreview("");
      setLogoModalOpen(false);
      toast.success("Logo updated.");
    } catch (error) {
      toast.error(error?.message || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  }, [
    pickedLogoFile,
    pickedLogoPreview,
    resolvedRestaurantId,
    updateStoredUserData,
  ]);

  const handleSaveUpi = useCallback(async () => {
    const value = String(upiValue || "").trim();
    if (!value) {
      toast.info("Enter UPI ID.");
      return;
    }
    if (!resolvedRestaurantId) {
      toast.error("Restaurant not found.");
      return;
    }
    setUpiSaving(true);
    try {
      const { result } = await tryPrinterProfileRequest("/printer/upi", () => {
        return {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: resolvedRestaurantId,
            upiaddress: value,
          }),
        };
      });
      const updated = updateStoredUserData({
        ...result.data,
        upiaddress: result?.data?.upiaddress || value,
      });
      setRestInfo(buildRestInfoFromUserData(updated || result.data || {}));
      setUpiModalOpen(false);
      toast.success("UPI saved.");
    } catch (error) {
      toast.error(error?.message || "Unable to save UPI");
    } finally {
      setUpiSaving(false);
    }
  }, [resolvedRestaurantId, upiValue, updateStoredUserData]);

  const handleSaveBillMeta = useCallback(async () => {
    const billNo = String(editBillNo || "").trim();
    if (!billNo) {
      toast.info("Bill number is required.");
      return;
    }
    if (!resolvedRestaurantId || !order?.id) {
      toast.error("Unable to update bill details.");
      return;
    }
    const merged = mergeDateAndTime(editBillDate, editBillTime);
    if (!merged || merged > new Date()) {
      toast.info("Select a valid bill date and time.");
      return;
    }
    setSavingBillMeta(true);
    try {
      const response = await fetch(`${API_BASE}/order/update-meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: resolvedRestaurantId,
          orderid: order.id,
          billno: billNo,
          billdate: formatBillDateForApi(merged),
          billtime: formatBillTimeForApi(merged),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Unable to update bill details");
      }
      const updatedMeta = result?.data || result?.order || result?.orderdetails || {};
      setOrder((prev) => ({
        ...(prev || {}),
        ...updatedMeta,
        ordercode: updatedMeta?.ordercode || billNo,
        billno: updatedMeta?.billno || billNo,
        created_at: updatedMeta?.created_at || merged.toISOString(),
      }));
      setOrderDetailResponse((prev) => ({
        ...(prev || {}),
        orderdetails: {
          ...(prev?.orderdetails || {}),
          ...updatedMeta,
          ordercode: updatedMeta?.ordercode || billNo,
          billno: updatedMeta?.billno || billNo,
          created_at: updatedMeta?.created_at || merged.toISOString(),
        },
      }));
      setBillMetaModalOpen(false);
      toast.success("Bill details updated.");
    } catch (error) {
      toast.error(error?.message || "Unable to update bill details");
    } finally {
      setSavingBillMeta(false);
    }
  }, [editBillDate, editBillNo, editBillTime, order, resolvedRestaurantId]);

  const handleDownloadInvoicePdf = useCallback(async () => {
    if (!receiptRef.current) {
      toast.info("Invoice preview not ready.");
      return;
    }
    try {
      await downloadPDF(receiptRef.current, invoiceFileName);
      toast.success("Invoice PDF downloaded.");
    } catch (error) {
      toast.error(error?.message || "Unable to download PDF");
    }
  }, [invoiceFileName]);

  const handleShareOnWhatsApp = useCallback(async () => {
    const digits = digitsOnly(whatsAppNumber);
    if (!digits) {
      toast.info("Enter WhatsApp number.");
      return;
    }
    setSharingInvoice(true);
    try {
      if (receiptRef.current) {
        await downloadPDF(receiptRef.current, invoiceFileName);
      }
      const url = `https://wa.me/${digits}?text=${encodeURIComponent(
        whatsAppSummaryText
      )}`;
      window.open(url, "_blank", "noopener,noreferrer");
      setWhatsAppModalOpen(false);
      toast.success("WhatsApp opened. Attach the downloaded PDF if needed.");
    } catch (error) {
      toast.error(error?.message || "Unable to prepare WhatsApp invoice");
    } finally {
      setSharingInvoice(false);
    }
  }, [invoiceFileName, whatsAppNumber, whatsAppSummaryText]);

  const handleRechargeSupport = useCallback(() => {
    openSupportWhatsApp(
      `Hi, I want to recharge QR credits.\nRestaurant ID: ${resolvedRestaurantId || "-"}\nRequested amount: ₹${Number(
        rechargeAmount || 0
      ).toFixed(0)}\nCurrent remaining QR credits: ${Number(
        qrEntitlement?.remaining ?? 0
      )}`
    );
  }, [qrEntitlement?.remaining, rechargeAmount, resolvedRestaurantId]);

  const handlePhonePeRecharge = useCallback(() => {
    navigate("/plan-selection");
  }, [navigate]);


  /* ===== UI ===== */
  return (
    <Page $paper={paperMeta}>
      <PageGlow aria-hidden="true" />
      <Toolbar $paper={paperMeta}>
        <ToolbarLeft>
          <ToolbarTitle>Print Preview</ToolbarTitle>
          <ToolbarSub>
            Bill No. {meta.code} • {meta.tablePrefix || "Table"} {meta.table}
          </ToolbarSub>
        </ToolbarLeft>
        <ToolbarActions>
          <SecondaryBtn onClick={() => navigate(-1)}>Back</SecondaryBtn>
        </ToolbarActions>
      </Toolbar>
      <WorkspaceGrid $hasOrder={!loading && hasOrder}>
        <PreviewColumn>
          <StatusStack>
            {bleStatus ? <BleStatus>{bleStatus}</BleStatus> : null}
            {usbStatus ? <BleStatus>{usbStatus}</BleStatus> : null}
          </StatusStack>

          <ReceiptWrapper $paper={paperMeta}>
            {loading ? (
              <ReceiptCard $paper={paperMeta}>
                <p>Loading order details…</p>
              </ReceiptCard>
            ) : error ? (
              <ReceiptCard $paper={paperMeta}>
                <ErrorTitle>Error</ErrorTitle>
                <p>{error}</p>
              </ReceiptCard>
            ) : !hasOrder ? (
              <ReceiptCard $paper={paperMeta}>
                <ErrorTitle>No order data</ErrorTitle>
                <p>Couldn&apos;t load order #{orderId}</p>
              </ReceiptCard>
            ) : (
              <ReceiptCard $paper={paperMeta} ref={receiptRef}>
                {/* TOP STRIP BADGE */}
                <TopStrip $paper={paperMeta}>
                  <TopStripLine />
                  <TopStripLabel>TAX INVOICE</TopStripLabel>
                  <TopStripLine />
                </TopStrip>

                {/* HEADER: Restaurant + Bill meta */}
                <ReceiptHeader $paper={paperMeta}>
                  <HeaderLeft>
                    {showLogoOnBillSetting && !!logoUrl ? (
                      <Logo>
                        <img src={logoUrl} alt="Logo" />
                      </Logo>
                    ) : null}
                    <div>
                      <RestName
                        $bold={boldRestaurantNameSetting}
                        style={{
                          fontSize: `${sizeToTextRem(businessNameSizeSetting, 1.34)}rem`,
                        }}
                      >
                        {restInfo.restaurant_name}
                      </RestName>
                      {restInfo.addressline1 && (
                        <RestLine>{restInfo.addressline1}</RestLine>
                      )}
                      {restInfo.addressline2 && (
                        <RestLine>{restInfo.addressline2}</RestLine>
                      )}
                      {restInfo.addressline3 && (
                        <RestLine>{restInfo.addressline3}</RestLine>
                      )}
                      {showPhoneOnBill && restInfo.mobile_number && (
                        <RestMeta>Ph: {restInfo.mobile_number}</RestMeta>
                      )}
                      {restInfo.gstno && (
                        <RestMeta>{taxRegistrationLabel}: {restInfo.gstno}</RestMeta>
                      )}
                    </div>
                  </HeaderLeft>

                  <HeaderRight $paper={paperMeta}>
                    <MetaChip>
                      <MetaLabel>Bill No</MetaLabel>
                      <MetaValue>{meta.code}</MetaValue>
                    </MetaChip>
                    <MetaChip>
                      <MetaLabel>Date</MetaLabel>
                      <MetaValue>{formatDateTime(meta.createdAt)}</MetaValue>
                    </MetaChip>
                    <MetaChip>
                      <MetaLabel>{meta.tablePrefix || "Table"}</MetaLabel>
                      <MetaValue>{meta.table}</MetaValue>
                    </MetaChip>
                    {showPaymentModeOnBillSetting ? (
                      <MetaChip>
                        <MetaLabel>Payment</MetaLabel>
                        <MetaValue>{getOrderPaymentMode(order) || "N/A"}</MetaValue>
                      </MetaChip>
                    ) : null}
                  </HeaderRight>
                </ReceiptHeader>

                {/* CUSTOMER / PAYMENT */}
                {customFieldRows.length > 0 || customerDisplay ? (
                  <Section>
                    <SectionRow>
                      {customFieldRows.map((row) => (
                        <SectionItem key={row.label}>
                          <Label>{row.label}</Label>
                          <SectionValue>{row.value}</SectionValue>
                        </SectionItem>
                      ))}
                      {customerDisplay && (
                        <SectionItem>
                          <Label>Customer</Label>
                          <SectionValue>{customerDisplay}</SectionValue>
                        </SectionItem>
                      )}
                    </SectionRow>
                  </Section>
                ) : null}

                <Divider />

                {/* ITEMS */}
                <ItemsTable $paper={paperMeta}>
                  <thead>
                    <tr>
                      <th style={{ width: "48%" }}>Item</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayItems.map((it, idx) => {
                      const qty = N(it.qty || it.quantity || it.qtyy || 0);
                      const rate = N(
                        it.priceaftertax ??
                          it.price_after_tax ??
                          it.rate_with_tax ??
                          it.price ??
                          0
                      );
                      const name =
                        it.full_name ||
                        it.product_name ||
                        it.productname ||
                        it.name ||
                        "Item";
                      return (
                        <tr key={idx}>
                          <td>{name}</td>
                          <td>{qty}</td>
                          <td>{currencySymbol}{money(rate)}</td>
                          <td>{currencySymbol}{money(qty * rate)}</td>
                        </tr>
                      );
                    })}
                    {!displayItems.length && (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center" }}>
                          No items for this order
                        </td>
                      </tr>
                    )}
                  </tbody>
                </ItemsTable>

                {/* TOTALS + GST */}
                <BottomArea $paper={paperMeta}>
                  <BottomLeft>
                    {(gstEnabled || gstVisible) && showTaxBreakupOnBillSetting && taxRows.rows.length > 0 && (
                      <GSTBreakup $paper={paperMeta}>
                        <GSTTitle>{displayTaxLabel} Breakup</GSTTitle>
                        <div className="line head">
                          <span>Taxable</span>
                          <span>
                            {taxRows.splitTaxBreakup
                              ? formatSplitTaxLabel("CGST", gstSplitRate, {
                                  showRate: false,
                                })
                              : displayTaxLabel}
                          </span>
                          <span>
                            {taxRows.splitTaxBreakup
                              ? formatSplitTaxLabel("SGST", gstSplitRate, {
                                  showRate: false,
                                })
                              : ""}
                          </span>
                          <span>Total</span>
                        </div>
                        {taxRows.rows.map((r, i) => (
                          <div className="line" key={i}>
                            <span>{currencySymbol}{money(r.taxable)}</span>
                            <span>
                              {currencySymbol}{money(taxRows.splitTaxBreakup ? r.cgst : r.total)}
                            </span>
                            <span>{taxRows.splitTaxBreakup ? `${currencySymbol}${money(r.sgst)}` : ""}</span>
                            <span>{currencySymbol}{money(r.total)}</span>
                          </div>
                        ))}
                      </GSTBreakup>
                    )}

                    {qrInfo && (
                      <QRRow>
                        <QRText>
                          <Label>{qrInfo.label}</Label>
                          {qrInfo.type === "upi" ? (
                            <>
                              <QRSub>UPI: {qrInfo.upiId}</QRSub>
                              <QRAmount>Amount: {currencySymbol}{qrInfo.amountStr}</QRAmount>
                            </>
                          ) : (
                            <QRSub>Scan QR to see details</QRSub>
                          )}
                        </QRText>
                        <QRBox>
                          <img src={qrInfo.qrImg} alt="QR" />
                        </QRBox>
                      </QRRow>
                    )}
                  </BottomLeft>

                  <TotalsCard $paper={paperMeta}>
                    <TotalsRow>
                      <span>Subtotal</span>
                      <span>{currencySymbol}{money(gstVisible && totals.gst > 0 && totals.subtotal >= totals.gst ? (totals.subtotal - totals.gst) : totals.subtotal)}</span>
                    </TotalsRow>
                    {gstVisible && (
                      <>
                        {taxRows.splitTaxBreakup ? (
                          <>
                            <TotalsRow>
                              <span>{formatSplitTaxLabel("CGST", gstSplitRate)}</span>
                              <span>{currencySymbol}{money(taxRowsTotals.cgst)}</span>
                            </TotalsRow>
                            <TotalsRow>
                              <span>{formatSplitTaxLabel("SGST", gstSplitRate)}</span>
                              <span>{currencySymbol}{money(taxRowsTotals.sgst)}</span>
                            </TotalsRow>
                          </>
                        ) : (
                          <TotalsRow>
                            <span>{displayTaxLabel}</span>
                            <span>{currencySymbol}{money(totals.gst)}</span>
                          </TotalsRow>
                        )}
                      </>
                    )}
                    {totals.discount > 0 && (
                      <TotalsRow>
                        <span>Discount</span>
                        <span
                          style={{
                            fontSize: `${sizeToTextRem(savingAmountSizeSetting, 1)}rem`,
                          }}
                        >
                          -{currencySymbol}{money(totals.discount)}
                        </span>
                      </TotalsRow>
                    )}
                    {deliveryEnabled && deliveryCharge > 0 && (
                      <TotalsRow>
                        <span>Delivery</span>
                        <span>{currencySymbol}{money(deliveryCharge)}</span>
                      </TotalsRow>
                    )}
                    <TotalsDivider />
                    <TotalsRow bold $forceBold={boldGrandTotalSetting}>
                      <span>Grand Total</span>
                      <span
                        style={{
                          fontSize: `${sizeToTextRem(totalAmountSizeSetting, 1.14)}rem`,
                        }}
                      >
                        {currencySymbol}{money(totals.grand)}
                      </span>
                    </TotalsRow>
                  </TotalsCard>
                </BottomArea>

                {showThanksOnBillSetting ? (
                  <FooterNote $paper={paperMeta}>Thank you for dining with us!</FooterNote>
                ) : null}
              </ReceiptCard>
            )}
          </ReceiptWrapper>
        </PreviewColumn>

        {!loading && hasOrder ? (
          <ActionsColumn>
            <ActionHero>
              <ActionHeroEyebrow>Invoice Controls</ActionHeroEyebrow>
              <ActionHeroTitle>{restInfo.restaurant_name || "Restaurant"}</ActionHeroTitle>
              <ActionHeroMeta>
                <ActionPill>Bill #{meta.code}</ActionPill>
                <ActionPill>{meta.tablePrefix || "Table"} {meta.table}</ActionPill>
                <ActionPill>Grand Total {currencySymbol}{money(totals.grand)}</ActionPill>
              </ActionHeroMeta>
            </ActionHero>

            <FooterActionsWrap>
              <FooterGroup>
                <FooterGroupTitle>Bill Actions</FooterGroupTitle>
                <FooterGroupHint>Edit, export, ya share invoice in one place.</FooterGroupHint>
                <FooterButtonRow>
                  <SecondaryBtn onClick={() => setBillMetaModalOpen(true)}>
                    Edit Bill Details
                  </SecondaryBtn>
                  <SecondaryBtn onClick={handleDownloadInvoicePdf}>
                    Download PDF
                  </SecondaryBtn>
                  <SecondaryBtn onClick={() => setWhatsAppModalOpen(true)}>
                    WhatsApp Invoice
                  </SecondaryBtn>
                </FooterButtonRow>
              </FooterGroup>

              <FooterGroup $accent>
                <FooterGroupTitle>Print Actions</FooterGroupTitle>
                <FooterGroupHint>Browser preview ya direct thermal printer, dono yahin se.</FooterGroupHint>
                <FooterButtonRow>
                  <PrimaryBtn onClick={() => window.print()}>Open Print Preview</PrimaryBtn>
                  <SecondaryBtn onClick={handleBlePrint} disabled={blePrinting || usbPrinting}>
                    {blePrinting ? "Printing…" : "Thermal Print (BLE)"}
                  </SecondaryBtn>
                  <SecondaryBtn onClick={handleUsbPrint} disabled={blePrinting || usbPrinting}>
                    {usbPrinting ? "Printing…" : "Thermal Print (USB)"}
                  </SecondaryBtn>
                </FooterButtonRow>
              </FooterGroup>

              <FooterGroup>
                <FooterGroupTitle>Setup</FooterGroupTitle>
                <FooterMetaLine>
                  <span>Logo: {restInfo.avatar_url ? "Added" : "Not Added"}</span>
                  <span>UPI: {restInfo.upiaddress ? "Ready" : "Missing"}</span>
                </FooterMetaLine>
                <FooterButtonRow>
                  <SecondaryBtn onClick={() => setLogoModalOpen(true)}>Upload Logo</SecondaryBtn>
                  <SecondaryBtn onClick={() => setUpiModalOpen(true)}>Edit UPI</SecondaryBtn>
                </FooterButtonRow>
              </FooterGroup>
            </FooterActionsWrap>
          </ActionsColumn>
        ) : null}
      </WorkspaceGrid>

      <ModalOverlay
        $open={logoModalOpen}
        onClick={() => !logoUploading && setLogoModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Upload Logo</ModalTitle>
          <ModalSub>Upload a square logo to show on printed bills.</ModalSub>
          <FilePickerWrap>
            <input type="file" accept="image/*" onChange={handleLogoFileChange} />
          </FilePickerWrap>
          <LogoPreviewBox>
            {pickedLogoPreview || restInfo.avatar_url ? (
              <img src={pickedLogoPreview || logoUrl} alt="Logo preview" />
            ) : (
              <PreviewEmpty>No logo selected</PreviewEmpty>
            )}
          </LogoPreviewBox>
          <ModalActions>
            <SecondaryBtn onClick={() => setLogoModalOpen(false)} disabled={logoUploading}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleUploadLogo} disabled={logoUploading}>
              {logoUploading ? "Uploading…" : "Save Logo"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay
        $open={upiModalOpen}
        onClick={() => !upiSaving && setUpiModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Edit UPI</ModalTitle>
          <ModalSub>Update the UPI ID used on printed bills and QR.</ModalSub>
          <ModalField>
            <ModalLabel>UPI ID</ModalLabel>
            <ModalInput
              value={upiValue}
              onChange={(e) => setUpiValue(e.target.value)}
              placeholder="name@bank"
            />
          </ModalField>
          <ModalActions>
            <SecondaryBtn onClick={() => setUpiModalOpen(false)} disabled={upiSaving}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleSaveUpi} disabled={upiSaving}>
              {upiSaving ? "Saving…" : "Save UPI"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay
        $open={billMetaModalOpen}
        onClick={() => !savingBillMeta && setBillMetaModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Edit Bill Details</ModalTitle>
          <ModalSub>Update bill number, date or time for this order.</ModalSub>
          <ModalGrid>
            <ModalField>
              <ModalLabel>Bill Number</ModalLabel>
              <ModalInput
                value={editBillNo}
                onChange={(e) => setEditBillNo(e.target.value)}
                placeholder="Bill no"
              />
            </ModalField>
            <ModalField>
              <ModalLabel>Bill Date</ModalLabel>
              <ModalInput
                type="date"
                value={editBillDate}
                onChange={(e) => setEditBillDate(e.target.value)}
              />
            </ModalField>
            <ModalField>
              <ModalLabel>Bill Time</ModalLabel>
              <ModalInput
                type="time"
                value={editBillTime}
                onChange={(e) => setEditBillTime(e.target.value)}
              />
            </ModalField>
          </ModalGrid>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #e2e8f0", display: "grid", gap: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.9rem", color: "#1e293b", fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={boldRestaurantNameSetting}
                onChange={(e) => {
                  setBoldRestaurantNameSetting(e.target.checked);
                  localStorage.setItem("printer_setting_boldRestaurantName", String(e.target.checked));
                }}
              />
              Bold Restaurant Name on Bill
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.9rem", color: "#1e293b", fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={boldGrandTotalSetting}
                onChange={(e) => {
                  setBoldGrandTotalSetting(e.target.checked);
                  localStorage.setItem("printer_setting_boldGrandTotal", String(e.target.checked));
                }}
              />
              Bold Grand Total on Bill
            </label>
          </div>
          <ModalActions>
            <SecondaryBtn onClick={() => setBillMetaModalOpen(false)} disabled={savingBillMeta}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleSaveBillMeta} disabled={savingBillMeta}>
              {savingBillMeta ? "Saving…" : "Save Bill"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay
        $open={whatsAppModalOpen}
        onClick={() => !sharingInvoice && setWhatsAppModalOpen(false)}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>WhatsApp Invoice</ModalTitle>
          <ModalSub>
            PDF download hoga, phir WhatsApp chat open hogi. PDF attach karke customer ko send kar sakte ho.
          </ModalSub>
          <ModalField>
            <ModalLabel>WhatsApp Number</ModalLabel>
            <ModalInput
              value={whatsAppNumber}
              onChange={(e) => setWhatsAppNumber(digitsOnly(e.target.value))}
              placeholder="91XXXXXXXXXX"
            />
          </ModalField>
          <MessagePreview>{whatsAppSummaryText}</MessagePreview>
          <ModalActions>
            <SecondaryBtn onClick={() => setWhatsAppModalOpen(false)} disabled={sharingInvoice}>
              Cancel
            </SecondaryBtn>
            <PrimaryBtn onClick={handleShareOnWhatsApp} disabled={sharingInvoice}>
              {sharingInvoice ? "Preparing…" : "Download + Open WhatsApp"}
            </PrimaryBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </Page>
  );
};

export default PrintScreen;

/* ===== Styled Components (WOW + Print friendly) ===== */

const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(15, 118, 186, 0.14), transparent 26%),
    radial-gradient(circle at top right, rgba(245, 158, 11, 0.1), transparent 20%),
    linear-gradient(180deg, #f3f7fb 0%, #e7eef6 52%, #edf3f9 100%);
  padding: 28px 18px 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow-x: hidden;
  font-family: "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;

  @media print {
    background: #ffffff !important;
    padding: 0;
    @page {
      size: ${({ $paper }) => $paper?.printSize || "58mm auto"};
      margin: ${({ $paper }) => $paper?.printMargin || "4mm"};
    }
  }
`;

const PageGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.55), transparent 42%),
    linear-gradient(315deg, rgba(255, 255, 255, 0.35), transparent 28%);

  @media print {
    display: none;
  }
`;

const Toolbar = styled.div`
  width: 100%;
  max-width: 1220px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  margin-bottom: 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow:
    0 20px 50px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(18px);
  padding: 18px 20px;
  position: relative;
  z-index: 1;

  @media print {
    display: none;
  }
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToolbarTitle = styled.div`
  text-transform: none;
  letter-spacing: 0.01em;
  font-size: 1.2rem;
  font-weight: 900;
  color: #0f172a;
`;

const ToolbarSub = styled.small`
  color: #4b647d;
  font-size: 0.9rem;
  font-weight: 700;
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const WorkspaceGrid = styled.div`
  width: 100%;
  max-width: 1220px;
  display: grid;
  grid-template-columns: ${({ $hasOrder }) =>
    $hasOrder ? "minmax(360px, 520px) minmax(320px, 1fr)" : "minmax(360px, 520px)"};
  align-items: start;
  justify-content: center;
  gap: 24px;
  position: relative;
  z-index: 1;

  @media print {
    display: block;
    max-width: ${({ $paper }) => $paper?.printWidth || "58mm"};
  }

  @media (max-width: 1120px) {
    grid-template-columns: 1fr;
    max-width: ${({ $hasOrder }) => ($hasOrder ? "860px" : "520px")};
  }
`;

const PreviewColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionsColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 18px;

  @media print {
    display: none;
  }

  @media (max-width: 1120px) {
    position: static;
  }
`;

const ActionHero = styled.div`
  border-radius: 28px;
  padding: 22px 22px 20px;
  background:
    linear-gradient(135deg, rgba(9, 74, 120, 0.96), rgba(17, 114, 169, 0.92)),
    linear-gradient(180deg, rgba(255, 255, 255, 0.16), transparent);
  color: #f8fbff;
  box-shadow: 0 24px 44px rgba(15, 23, 42, 0.18);
  overflow: hidden;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: auto -40px -60px auto;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ActionHeroEyebrow = styled.div`
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 800;
  color: rgba(236, 253, 245, 0.78);
  position: relative;
  z-index: 1;
`;

const ActionHeroTitle = styled.h2`
  margin: 8px 0 0;
  font-size: 1.5rem;
  line-height: 1.1;
  font-weight: 900;
  position: relative;
  z-index: 1;
`;

const ActionHeroMeta = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  position: relative;
  z-index: 1;
`;

const ActionPill = styled.div`
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #f8fbff;
  font-size: 0.83rem;
  font-weight: 700;
  backdrop-filter: blur(8px);
`;

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media print {
    display: none;
  }
`;

const BleStatus = styled.div`
  width: 100%;
  color: #16324d;
  font-size: 0.87rem;
  font-weight: 700;
  border: 1px solid rgba(125, 170, 214, 0.42);
  background: rgba(255, 255, 255, 0.82);
  border-radius: 16px;
  padding: 12px 14px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(14px);
`;

const FooterActionsWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media print {
    display: none;
  }
`;

const FooterGroup = styled.div`
  background: ${({ $accent }) =>
    $accent
      ? "linear-gradient(180deg, rgba(237, 248, 255, 0.98), rgba(255, 255, 255, 0.98))"
      : "rgba(255, 255, 255, 0.88)"};
  border: 1px solid
    ${({ $accent }) => ($accent ? "rgba(59, 130, 246, 0.2)" : "rgba(203, 213, 225, 0.65)")};
  border-radius: 24px;
  padding: 18px;
  box-shadow:
    0 18px 36px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.64);
  display: flex;
  flex-direction: column;
  gap: 12px;
  backdrop-filter: blur(16px);
`;

const FooterGroupTitle = styled.div`
  color: #0f172a;
  font-size: 0.94rem;
  font-weight: 900;
  letter-spacing: 0.02em;
`;

const FooterGroupHint = styled.div`
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
  font-weight: 600;
`;

const CreditMetaRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const CreditMetaBox = styled.div`
  border: 1px solid #dbe5ef;
  border-radius: 10px;
  padding: 10px;
  background: #f8fbff;
`;

const CreditValue = styled.div`
  margin-top: 3px;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 900;
`;

const RechargeChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FooterButtonRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  > button {
    width: 100%;
  }

  @media (max-width: 1320px) {
    grid-template-columns: 1fr;
  }
`;

const FooterMetaLine = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  color: #516173;
  font-size: 0.82rem;
  font-weight: 700;

  span {
    padding: 8px 12px;
    border-radius: 999px;
    background: #f8fbff;
    border: 1px solid #d8e3ef;
  }
`;

const RechargeChip = styled.button`
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? "#015b9a" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#015b9a" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#0f172a")};
  font-size: 0.76rem;
  font-weight: 800;
  padding: 7px 11px;
  cursor: pointer;
`;

const ReceiptWrapper = styled.div`
  width: 100%;
  max-width: ${({ $paper }) =>
    $paper?.key === "a4"
      ? "720px"
      : $paper?.key === "4in"
      ? "520px"
      : $paper?.key === "3in"
      ? "460px"
      : "400px"};
  margin: 0 auto;

  @media print {
    max-width: ${({ $paper }) => $paper?.printWidth || "58mm"};
  }
`;

const ReceiptCard = styled.div`
  background: #ffffff;
  border-radius: ${({ $paper }) => ($paper?.key === "a4" ? "24px" : $paper?.key === "3in" ? "20px" : "16px")};
  padding: ${({ $paper }) =>
    $paper?.key === "a4"
      ? "24px 28px"
      : $paper?.key === "3in"
      ? "18px 18px 22px"
      : "14px 14px 18px"};
  box-shadow:
    0 24px 60px rgba(15, 23, 42, 0.16),
    0 6px 18px rgba(15, 23, 42, 0.08);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(203, 213, 225, 0.82);

  &::before {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    top: 0;
    height: 10px;
    background:
      radial-gradient(circle at 10px 5px, transparent 5px, #eef2f6 5.5px) left top / 20px 10px repeat-x;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 0;
    height: 10px;
    background:
      radial-gradient(circle at 10px 5px, transparent 5px, #eef2f6 5.5px) left bottom / 20px 10px repeat-x;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media print {
    box-shadow: none !important;
    border-radius: 0 !important;
    border: none !important;
    padding: ${({ $paper }) => ($paper?.key === "3in" ? "4mm 2mm" : "2mm")} !important;
    max-width: ${({ $paper }) => $paper?.printWidth || "58mm"} !important;
    background: #ffffff !important;
    color: #000000 !important;
    &::before {
      display: none !important;
    }
    &::after {
      display: none !important;
    }
  }
`;

const TopStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  width: 100%;
`;

const TopStripLine = styled.div`
  flex: 1;
  height: 1px;
  background: #cbd5e1;
  @media print {
    background: #000000 !important;
    height: 1px !important;
  }
`;

const TopStripLabel = styled.div`
  padding: 3px 14px;
  border-radius: 999px;
  border: 1px dashed #0f172a;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0f172a;
  background: #f8fafc;
  white-space: nowrap;

  @media print {
    border: 1px solid #000000 !important;
    color: #000000 !important;
    background: transparent !important;
    font-size: 0.7rem !important;
  }
`;

const ReceiptHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: flex-start;
  padding-bottom: 12px;
  border-bottom: 1.5px dashed #cbd5e1;

  @media print {
    border-bottom: 1.5px solid #000000 !important;
    gap: 8px !important;
    padding-bottom: 8px !important;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  align-items: stretch;
  width: 100%;

  @media print {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 4px !important;
  }

  @media (max-width: 768px) {
    width: 100%;
    grid-template-columns: 1fr 1fr;
  }
`;

const Logo = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #d7dbe2;
  display: grid;
  place-items: center;
  padding: 4px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media print {
    width: 48px !important;
    height: 48px !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    border-color: #000000 !important;
    padding: 2px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    img {
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      display: block !important;
      object-fit: contain !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

const RestName = styled.h1`
  margin: 0;
  font-size: 1rem;
  font-weight: ${({ $bold }) => ($bold !== false ? 900 : 600)};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #000000;
  text-align: center;
  -webkit-text-stroke: ${({ $bold }) => ($bold !== false ? "0.75px #000000" : "none")};
  text-shadow: ${({ $bold }) => ($bold !== false ? "0.3px 0 0 #000000, -0.3px 0 0 #000000" : "none")};

  @media print {
    color: #000000 !important;
    font-weight: ${({ $bold }) => ($bold !== false ? "900 !important" : "600 !important")};
    -webkit-text-stroke: ${({ $bold }) => ($bold !== false ? "0.75px #000000 !important" : "none !important")};
    text-shadow: ${({ $bold }) => ($bold !== false ? "0.3px 0 0 #000000 !important, -0.3px 0 0 #000000 !important" : "none !important")};
    text-align: center !important;
  }
`;

const RestLine = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: #334155;
  line-height: 1.25;

  @media print {
    color: #000000 !important;
  }
`;

const RestMeta = styled.p`
  margin: 2px 0 0 0;
  font-size: 0.76rem;
  color: #0f172a;
  font-weight: 700;

  @media print {
    color: #000000 !important;
  }
`;

const MetaChip = styled.div`
  border-radius: 12px;
  border: 1px solid #d8e0e8;
  padding: 6px 9px;
  min-width: 0;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media print {
    border: 1px solid #000000 !important;
    background: transparent !important;
    border-radius: 4px !important;
    padding: 3px 5px !important;
  }
`;

const MetaLabel = styled.div`
  font-size: 0.64rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #64748b;
  font-weight: 800;

  @media print {
    color: #000000 !important;
    font-size: 0.6rem !important;
  }
`;

const MetaValue = styled.div`
  font-size: 0.82rem;
  font-weight: 900;
  color: #0f172a;
  word-break: break-word;

  @media print {
    color: #000000 !important;
    font-size: 0.78rem !important;
  }
`;

const Section = styled.div`
  margin-top: 10px;
`;

const SectionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 6px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SectionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  border: 1px solid #dde6ef;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff, #fbfdff);
  padding: 7px 9px;

  @media print {
    border: 1px solid #000000 !important;
    background: transparent !important;
    border-radius: 4px !important;
  }
`;

const SectionValue = styled.div`
  font-size: 0.76rem;
  font-weight: 800;
  color: #111827;

  @media print {
    color: #000000 !important;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px dashed #cbd5e1;
  margin: 10px 0;

  @media print {
    border-top: 1px solid #000000 !important;
    margin: 8px 0 !important;
  }
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ $paper }) => ($paper?.key === "3in" ? "0.82rem" : "0.8rem")};
  margin-top: 6px;

  th,
  td {
    padding: ${({ $paper }) => ($paper?.key === "3in" ? "7px 6px" : "6px 4px")};
    border-bottom: 1px solid #eef2f7;
  }
  th {
    text-transform: uppercase;
    font-size: 0.66rem;
    letter-spacing: 0.12em;
    color: #334155;
    font-weight: 900;
    background: #f1f5f9;
    border-top: 1px solid #cbd5e1;
    border-bottom: 1.5px solid #94a3b8;
  }
  td {
    color: #0f172a;
    font-weight: 600;
  }
  td:nth-child(1) {
    font-weight: 700;
  }
  td:nth-child(2),
  th:nth-child(2) {
    width: 48px;
    text-align: center;
  }
  td:nth-child(3),
  td:nth-child(4),
  th:nth-child(3),
  th:nth-child(4) {
    text-align: right;
    width: 80px;
    font-variant-numeric: tabular-nums;
  }

  @media print {
    font-size: ${({ $paper }) => ($paper?.key === "3in" ? "0.8rem" : "0.74rem")} !important;
    margin-top: 4px !important;
    th,
    td {
      padding: 4px 2px !important;
      border-bottom: 1px solid #000000 !important;
      color: #000000 !important;
    }
    th {
      background: transparent !important;
      border-top: 1.5px solid #000000 !important;
      border-bottom: 1.5px solid #000000 !important;
      color: #000000 !important;
      font-weight: 900 !important;
    }
    td {
      font-weight: 700 !important;
    }
  }
`;

const BottomArea = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 12px;
`;

const BottomLeft = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GSTBreakup = styled.div`
  background: linear-gradient(180deg, #ffffff, #fbfdff);
  border-radius: 12px;
  border: 1px solid #dde5ef;
  padding: 8px 10px;
  font-size: 0.72rem;
  .line {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    text-align: center;
    font-weight: 700;
    color: #0f172a;
    padding: 2px 0;
    border-bottom: 1px dashed #f1f5f9;
  }
  .line:last-child {
    border-bottom: none;
  }
  .line.head {
    color: #475569;
    text-transform: uppercase;
    font-size: 0.62rem;
    letter-spacing: 0.1em;
    font-weight: 900;
    border-bottom: 1px solid #cbd5e1;
  }

  @media print {
    border: 1px solid #000000 !important;
    border-radius: 0 !important;
    background: transparent !important;
    padding: 4px !important;
    font-size: 0.68rem !important;
    .line {
      color: #000000 !important;
      border-bottom: 1px solid #000000 !important;
    }
    .line.head {
      color: #000000 !important;
      border-bottom: 1.5px solid #000000 !important;
    }
  }
`;

const GSTTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0f172a;
  margin-bottom: 4px;

  @media print {
    color: #000000 !important;
  }
`;

const QRRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  gap: 10px;
`;

const QRText = styled.div`
  min-width: 0;

  @media (max-width: 640px) {
    grid-column: 1 / -1;
  }
`;

const QRSub = styled.p`
  margin: 0;
  color: #334155;
  font-size: 0.76rem;

  @media print {
    color: #000000 !important;
  }
`;

const QRAmount = styled.p`
  margin: 0;
  color: #0f172a;
  font-weight: 900;
  font-size: 0.8rem;

  @media print {
    color: #000000 !important;
  }
`;

const QRBox = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 16px;
  border: 1px solid #d8dde6;
  background: linear-gradient(180deg, #ffffff, #fbfdff);
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media print {
    border: 1px solid #000000 !important;
    border-radius: 0 !important;
    background: transparent !important;
    width: 90px !important;
    height: 90px !important;
  }
`;

const TotalsCard = styled.div`
  border-radius: 14px;
  border: 1.5px solid #cbd5e1;
  padding: 10px 12px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);

  @media print {
    border: 1.5px solid #000000 !important;
    border-radius: 0 !important;
    background: transparent !important;
    padding: 6px 8px !important;
  }
`;

const TotalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${({ bold, $forceBold }) => ($forceBold === false ? 600 : bold ? 900 : 700)};
  color: ${({ bold }) => (bold ? "#000000" : "#334155")};
  font-size: ${({ bold }) => (bold ? "0.95rem" : "0.82rem")};
  margin: 3px 0;
  padding: ${({ bold }) => (bold ? "4px 0 2px" : "0")};
  -webkit-text-stroke: ${({ bold, $forceBold }) => ($forceBold !== false && bold ? "0.7px #000000" : "none")};
  text-shadow: ${({ bold, $forceBold }) => ($forceBold !== false && bold ? "0.3px 0 0 #000000, -0.3px 0 0 #000000" : "none")};

  @media print {
    color: #000000 !important;
    font-weight: ${({ bold, $forceBold }) => ($forceBold === false ? "600 !important" : bold ? "900 !important" : "700 !important")};
    font-size: ${({ bold }) => (bold ? "0.9rem" : "0.78rem")} !important;
    -webkit-text-stroke: ${({ bold, $forceBold }) => ($forceBold !== false && bold ? "0.7px #000000 !important" : "none !important")};
    text-shadow: ${({ bold, $forceBold }) => ($forceBold !== false && bold ? "0.3px 0 0 #000000 !important, -0.3px 0 0 #000000 !important" : "none !important")};
  }
`;

const TotalsDivider = styled.div`
  border-top: 1.5px dashed #94a3b8;
  margin: 6px 0;

  @media print {
    border-top: 1.5px solid #000000 !important;
  }
`;

const FooterNote = styled.div`
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px dashed #cbd5e1;
  text-align: center;
  color: #334155;
  font-weight: 800;
  font-size: 0.78rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;

  @media print {
    border-top: 1px solid #000000 !important;
    color: #000000 !important;
    font-size: 0.74rem !important;
    margin-top: 10px !important;
  }
`;

const Label = styled.span`
  font-size: 0.66rem;
  font-weight: 900;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.14em;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, #0c6ca9 0%, #0a4f7b 100%);
  color: #fff;
  border: 1px solid rgba(7, 83, 131, 0.92);
  padding: 13px 16px;
  border-radius: 16px;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  letter-spacing: 0.02em;
  text-transform: none;
  box-shadow: 0 14px 28px rgba(10, 79, 123, 0.22);
  transition: transform 0.12s ease, filter 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 18px 32px rgba(10, 79, 123, 0.26);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    filter: none;
  }
`;

const SecondaryBtn = styled.button`
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  color: #0d5f96;
  border: 1px solid #bfd3e6;
  padding: 13px 16px;
  border-radius: 16px;
  font-weight: 800;
  font-size: 0.88rem;
  cursor: pointer;
  letter-spacing: 0.02em;
  text-transform: none;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
  transition: transform 0.12s ease, background 0.2s ease, border-color 0.2s ease;
  &:hover {
    background: #f1f8fe;
    border-color: #8bb6d8;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorTitle = styled.h2`
  margin: 0 0 4px 0;
  font-size: 1rem;
  color: #b91c1c;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background:
    radial-gradient(circle at top, rgba(15, 118, 186, 0.14), transparent 28%),
    rgba(15, 23, 42, 0.52);
  backdrop-filter: blur(10px);
  display: ${({ $open }) => ($open ? "grid" : "none")};
  place-items: center;
  padding: 24px 16px;
  z-index: 50;
`;

const ModalCard = styled.div`
  width: min(100%, 560px);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 251, 255, 0.98));
  border-radius: 28px;
  border: 1px solid rgba(203, 213, 225, 0.85);
  box-shadow:
    0 32px 80px rgba(15, 23, 42, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.82);
  padding: 26px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 120px;
    background:
      radial-gradient(circle at top right, rgba(14, 116, 189, 0.14), transparent 52%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.72), transparent);
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 640px) {
    padding: 20px;
    border-radius: 24px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 900;
  letter-spacing: -0.02em;
`;

const ModalSub = styled.p`
  margin: 8px 0 0;
  color: #5f7084;
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 44ch;
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ModalField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 14px;
`;

const ModalLabel = styled.span`
  color: #425569;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

const ModalInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d3dde8;
  border-radius: 16px;
  padding: 13px 14px;
  font-size: 0.9rem;
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff, #f9fbfe);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  &:focus {
    outline: none;
    border-color: #7eb6db;
    box-shadow:
      0 0 0 4px rgba(14, 116, 189, 0.12),
      inset 0 1px 2px rgba(15, 23, 42, 0.04);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 22px;
`;

const FilePickerWrap = styled.div`
  margin-top: 18px;
  padding: 14px;
  border-radius: 22px;
  border: 1px dashed #9db4ca;
  background:
    linear-gradient(180deg, rgba(248, 251, 255, 0.95), rgba(241, 247, 252, 0.95));

  input {
    width: 100%;
    border: none;
    border-radius: 16px;
    padding: 8px 4px;
    background: transparent;
    color: #24364b;
    font-size: 0.95rem;
  }

  input::file-selector-button {
    margin-right: 14px;
    border: 1px solid #0d5f96;
    border-radius: 14px;
    padding: 12px 16px;
    background: linear-gradient(180deg, #ffffff, #eff7ff);
    color: #0d5f96;
    font-weight: 800;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.12s ease;
  }

  input::file-selector-button:hover {
    background: #e4f0fb;
    transform: translateY(-1px);
  }
`;

const LogoPreviewBox = styled.div`
  margin-top: 18px;
  min-height: 260px;
  border: 1px solid #d8e2ee;
  border-radius: 24px;
  background:
    linear-gradient(180deg, #f9fbfe, #f4f8fc);
  display: grid;
  place-items: center;
  overflow: hidden;
  position: relative;

  &::before {
    content: "Logo Preview";
    position: absolute;
    top: 14px;
    left: 16px;
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 900;
    color: #7a8da2;
  }

  img {
    max-width: 78%;
    max-height: 78%;
    object-fit: contain;
    filter: drop-shadow(0 16px 28px rgba(15, 23, 42, 0.12));
  }
`;

const PreviewEmpty = styled.div`
  color: #8ba0b6;
  font-size: 0.92rem;
  font-weight: 700;
  margin-top: 24px;
`;

const MessagePreview = styled.pre`
  margin: 14px 0 0;
  padding: 12px;
  border-radius: 12px;
  background: #f8fbff;
  border: 1px solid #dbe5ef;
  color: #0f172a;
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  font-family: inherit;
`;
